import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { registerSW } from 'virtual:pwa-register';
import { createBackup, createCsv, parseBackup } from '../infrastructure/backup';
import { exportTextFile } from '../infrastructure/text-export';
import { BrowserNotificationAdapter, getStorageEstimate, requestPersistentStorage } from '../infrastructure/platform';
import { WebPushReminderAdapter } from '../infrastructure/web-push-reminders';
import { useI18n } from '../i18n/I18nContext';
import { OfficialHandoffReceipt } from './OfficialHandoffReceipt';
import type { MessageKey } from '../i18n/catalog';
import { mealMissingIngredients, mealPresentIngredients, mealTemplateDescription, mealTemplateTitle, recipeShoppingItemLabel } from '../i18n/domain-labels';
import { FOOD_CATEGORIES, MEAL_TEMPLATES, OFFICIAL_SAFETY_SOURCES } from '../content/catalog';
import { calculatePriority, sortByPriority, type PriorityLevel } from '../domain/priority';
import { hasVisibleNumber } from '../domain/visible-number';
import { storagePolicyFor } from '../domain/storage-policy';
import type {
  AuditRecord,
  FoodContainer,
  Freezer,
  InventoryBatchMutation,
  InventorySnapshot,
  QuantityUnit,
  RecipeShoppingList,
} from '../domain/model';
import {
  buildContextualRecipePath,
  type RecipeReturn,
} from '../config/recipe-links';
import type { ArchibaldHandoffStatus, ArchibaldReceiptRetrieval, HandoffTarget } from '../application/contracts';
import { BACKUP_FORMAT_VERSION } from '../domain/model';
import { AddContainerModal, ContainerDetailModal, DiscardModal, MoveModal, PartialModal } from './ContainerModals';
import { useApp } from './AppContext';
import { TemperaturePanel } from './TemperaturePanel';
import { PwaInstallButton } from './PwaInstallButton';
import { RecipeIdeasCta } from './RecipeIdeasCta';
import { LanguageSelector } from './LanguageSelector';
import { PublicMealDemo } from './PublicMealDemo';
import { HouseholdSharingPanel } from './HouseholdSharingPanel';
import { myDohmFederation } from '../infrastructure/my-dohm-federation';
import { myDohmPortalOrigin } from '../infrastructure/my-dohm-navigation';
import {
  ShoppingListHandoffModal,
  type ShoppingListHandoffView,
} from './ShoppingListHandoffModal';
import {
  MealPreparationModal,
  type ConfirmedRecipeLine,
} from './MealPreparationModal';
import { GuidedDemoTour, type GuidedDemoView } from './GuidedDemoTour';
import { reminderDeliveryNoticeKey } from './reminder-preferences';
import {
  Button,
  categoryLabel,
  ContainerCard,
  containerDisplayName,
  containerReference,
  EmptyState,
  Field,
  Icon,
  Modal,
  SectionHeader,
  SelectField,
  formatDate,
  formatQuantity,
  locationLabel,
  snapshotDisplayLabel,
  unitLabel,
} from './components';

type View = 'home' | 'fridge' | 'inventory' | 'priorities' | 'meals' | 'more';
const MY_DOHM_MENU_ENTRY = {
  contractVersion: 'dohm.my-dohm-menu.v2',
  href: myDohmPortalOrigin(window.location.origin),
} as const;
const HANDOFF_TARGET_STORAGE_KEY = 'frozen-save.handoff-target';
const readHandoffTarget = (): HandoffTarget => {
  try {
    return localStorage.getItem(HANDOFF_TARGET_STORAGE_KEY) === 'paniprix' ? 'paniprix' : 'archibald';
  } catch {
    return 'archibald';
  }
};
const rememberHandoffTarget = (target: HandoffTarget): void => {
  try {
    localStorage.setItem(HANDOFF_TARGET_STORAGE_KEY, target);
  } catch {
    // Le choix reste valable pour la session même si le stockage navigateur est indisponible.
  }
};
type QuickModal =
  | { type: 'detail' | 'partial' | 'move' | 'discard'; containerId: string }
  | null;

const handoffViewFromStatus = (status: ArchibaldHandoffStatus): ShoppingListHandoffView => {
  if (status.state === 'disconnected') return { stage: 'disconnected' };
  if (status.state === 'identity_pending') return { stage: 'identity_pending', expiresAt: status.expiresAt };
  if (status.state === 'ready') return { stage: 'available' };
  return { stage: status.access, fallbackUrl: status.fallbackUrl };
};

const handoffViewFromReceipt = (
  result: ArchibaldReceiptRetrieval,
): ShoppingListHandoffView | null => {
  if (result.state === 'none') return null;
  if (result.state === 'pending') {
    return {
      stage: 'awaiting_receipt',
      intentId: result.intentId,
      expiresAt: result.expiresAt,
    };
  }
  if (result.state === 'accepted') return { stage: 'accepted', intentId: result.intentId };
  return {
    stage: result.state === 'declined'
      ? 'receipt_declined'
      : result.state === 'expired'
        ? 'receipt_expired'
        : result.state === 'unavailable'
          ? 'receipt_unavailable'
          : 'receipt_invalid',
  };
};

const ACTIVE_STATUSES = new Set<FoodContainer['status']>(['draft', 'present', 'partial']);
const localToday = (): string => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
};

const stripRecipeReturnFromAddress = () => {
  const clean = new URL(window.location.href);
  for (const key of [
    'recipe_id',
    'ingredient_id',
    'template_id',
    'return_token',
    'fs_source',
    'fs_lang',
    'fs_return',
    'fs_contract',
  ]) {
    clean.searchParams.delete(key);
  }
  window.history.replaceState(null, '', `${clean.pathname}${clean.search}${clean.hash}`);
};

const isActive = (container: FoodContainer): boolean => ACTIVE_STATUSES.has(container.status);

export const usePwaUpdates = () => {
  const [offlineReady, setOfflineReady] = useState(false);
  const [needRefresh, setNeedRefresh] = useState(false);
  const updateServiceWorker = useRef<((reload?: boolean) => Promise<void>) | null>(null);

  useEffect(() => {
    let updateTimer: number | null = null;
    const update = registerSW({
      immediate: true,
      onOfflineReady: () => setOfflineReady(true),
      onNeedRefresh: () => setNeedRefresh(true),
      onRegisteredSW: (_url, registration) => {
        if (registration != null) {
          const checkForUpdate = () => void registration.update().catch(() => undefined);
          checkForUpdate();
          updateTimer = window.setInterval(checkForUpdate, 60 * 60 * 1_000);
        }
      },
    });
    updateServiceWorker.current = update;
    return () => {
      if (updateTimer != null) window.clearInterval(updateTimer);
      updateServiceWorker.current = null;
    };
  }, []);

  return {
    offlineReady,
    needRefresh,
    applyUpdate: async () => {
      if (updateServiceWorker.current != null) {
        await updateServiceWorker.current(true);
      }
    },
    dismissOfflineReady: () => setOfflineReady(false),
    dismissRefresh: () => setNeedRefresh(false),
  };
};

const PwaUpdateBanner = ({ pwa }: { pwa: ReturnType<typeof usePwaUpdates> }) => {
  const { t } = useI18n();
  return (
    <>
      {pwa.needRefresh ? (
        <div className="update-banner" role="status">
          <div><strong>{t('app.updateReady')}</strong><span>{t('app.updateWhenSafe')}</span></div>
          <Button onClick={() => void pwa.applyUpdate()}>{t('app.update')}</Button>
          <button type="button" className="icon-button" onClick={pwa.dismissRefresh} aria-label={t('app.later')}><Icon name="close" /></button>
        </div>
      ) : null}
    </>
  );
};

const SetupScreen = () => {
  const { service, perform, busyKeys, runtime } = useApp();
  const { t } = useI18n();
  const [formOpen, setFormOpen] = useState(false);
  const [householdName, setHouseholdName] = useState(() => t('setup.defaultHousehold'));
  const [freezerName, setFreezerName] = useState(() => t('setup.defaultFreezer'));
  const [equipmentKind, setEquipmentKind] = useState<Freezer['equipmentKind']>('four-star');
  const [locations, setLocations] = useState(() => t('setup.defaultFreezerLocations'));
  const [safetyAcknowledged, setSafetyAcknowledged] = useState(false);

  const setup = async (event: FormEvent) => {
    event.preventDefault();
    const created = await perform(
      'setup',
      () =>
        service.setup({
          householdName,
          freezerName,
          equipmentKind,
          locationNames: locations.split(/\r?\n/),
          safetyAcknowledged,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Paris',
        }),
      { success: t('setup.ready') },
    );
    if (created != null) {
      document.getElementById('contenu')?.scrollTo({ top: 0 });
      // Certains moteurs WebKit peuvent laisser persist() en attente tant que
      // l'autorisation n'est pas décidée. La création locale est déjà durable :
      // cette optimisation ne doit donc jamais bloquer l'accès à l'inventaire.
      void perform(
        'persistent-storage-after-setup',
        async () => {
          const storageStatus = await requestPersistentStorage();
          await service.savePersistentStorageStatus(storageStatus);
        },
        { silent: true },
      );
    }
  };

  if (!formOpen) {
    return (
      <main id="contenu" className="setup-welcome" aria-labelledby="setup-welcome-title">
        <section className="setup-welcome__card">
          <img className="official-brand-logo" src="/brand/frozen-save-logo.svg" alt="Frozen Save by DOHM" />
          <span className="test-version-badge">{t('app.localNoAccount')}</span>
          <p className="eyebrow">Frozen Save</p>
          <h1 id="setup-welcome-title">{t('setup.title')}</h1>
          <p className="setup-welcome__description">{t('setup.description')}</p>
          <LanguageSelector />
          <Button onClick={() => setFormOpen(true)} icon={<Icon name="plus" size={18} />}>
            {t('setup.createHousehold')}
          </Button>
          <PwaInstallButton />
          <HouseholdSharingPanel onboarding />
          <a className="button button--ghost" href={runtime.demoPath}>{t('setup.tryApp')}</a>
        </section>
      </main>
    );
  }

  return (
    <main id="contenu" className="setup-shell">
      <section className="setup-intro" aria-labelledby="setup-title">
        <img className="official-brand-logo" src="/brand/frozen-save-logo.svg" alt="Frozen Save by DOHM" />
        <span className="test-version-badge">{t('app.localDevice')}</span>
        <span className="eyebrow">{t('setup.localOnly')}</span>
        <h1 id="setup-title">{t('setup.headline')}</h1>
        <p>{t('setup.intro')}</p>
        <ol className="setup-steps">
          <li><strong>1</strong><span>{t('setup.step1')}</span></li>
          <li><strong>2</strong><span>{t('setup.step2')}</span></li>
          <li><strong>3</strong><span>{t('setup.step3')}</span></li>
        </ol>
      </section>
      <section className="setup-card" aria-label={t('setup.formAria')}>
        <LanguageSelector />
        <h2>{t('setup.configure')}</h2>
        <p className="muted">{t('setup.examples')}</p>
        <form className="form-stack" onSubmit={(event) => void setup(event)}>
          <Field
            label={t('setup.householdName')}
            value={householdName}
            onChange={(event) => setHouseholdName(event.target.value)}
            maxLength={120}
            required
          />
          <Field
            label={t('setup.applianceName')}
            value={freezerName}
            onChange={(event) => setFreezerName(event.target.value)}
            maxLength={120}
            required
          />
          <SelectField
            label={t('setup.applianceType')}
            value={equipmentKind}
            onChange={(event) => {
              const next = event.target.value as Freezer['equipmentKind'];
              setEquipmentKind(next);
              if (next === 'fridge' && freezerName === t('setup.defaultFreezer')) {
                setFreezerName(t('setup.defaultFridge'));
                setLocations(t('setup.defaultFridgeLocations'));
              } else if (next !== 'fridge' && freezerName === t('setup.defaultFridge')) {
                setFreezerName(t('setup.defaultFreezer'));
                setLocations(t('setup.defaultFreezerLocations'));
              }
            }}
            hint={t('setup.declarativeHint')}
          >
            <option value="four-star">{t('setup.fourStar')}</option>
            <option value="three-star">{t('setup.threeStar')}</option>
            <option value="fridge">{t('setup.fridge')}</option>
            <option value="unknown">{t('setup.unknown')}</option>
          </SelectField>
          <label className="field">
            <span className="field__label">{t('setup.locations')}</span>
            <textarea
              value={locations}
              onChange={(event) => setLocations(event.target.value)}
              rows={4}
              maxLength={1_000}
              aria-describedby="locations-hint"
            />
            <span className="field__hint" id="locations-hint">{t('setup.oneLocationPerLine')}</span>
          </label>
          <label className="check-row">
            <input
              type="checkbox"
              checked={safetyAcknowledged}
              onChange={(event) => setSafetyAcknowledged(event.target.checked)}
            />
            <span>
              {t('setup.safety', { temperature: equipmentKind === 'fridge' ? '+4 °C' : '−18 °C' })}
              <small>{t('setup.safetyDetail')}</small>
            </span>
          </label>
          <Button type="submit" disabled={busyKeys.has('setup')} icon={<Icon name="check" />}>
            {t('setup.submit')}
          </Button>
        </form>
        <div className="setup-demo">
          <span>{t('setup.tryPrompt')}</span>
          <a className="button button--ghost" href={runtime.demoPath}>{t('setup.openDemo')}</a>
        </div>
      </section>
    </main>
  );
};

const QuickExit = ({
  snapshot,
  prefilledContainerId,
}: {
  snapshot: InventorySnapshot;
  prefilledContainerId: string | null;
}) => {
  const i18n = useI18n();
  const { locale, t } = i18n;
  const { service, performMutation, busyKeys } = useApp();
  const prefilledContainer = snapshot.containers.find(
    (container) => container.id === prefilledContainerId && isActive(container),
  );
  const [serial, setSerial] = useState(
    prefilledContainer != null && hasVisibleNumber(prefilledContainer)
      ? String(prefilledContainer.serialNumber)
      : '',
  );
  const [selectedContainerId, setSelectedContainerId] = useState(
    prefilledContainer != null && !hasVisibleNumber(prefilledContainer)
      ? prefilledContainer.id
      : '',
  );
  const [quantityMode, setQuantityMode] = useState<'all' | 'one' | 'custom'>('all');
  const [customQuantity, setCustomQuantity] = useState('');
  const [secondaryOpen, setSecondaryOpen] = useState(false);
  const [freezerId, setFreezerId] = useState('');
  const result =
    (selectedContainerId === ''
      ? null
      : snapshot.containers.find(
          (container) => isActive(container) && container.id === selectedContainerId,
        )) ??
    (serial === ''
      ? null
      : snapshot.containers
          .filter(hasVisibleNumber)
          .find(
            (container) => isActive(container) && container.serialNumber === Number(serial),
          )) ??
    null;
  const activeContainers = snapshot.containers.filter(isActive);
  const freezerContainers = activeContainers.filter((container) => {
    const location = snapshot.locations.find((item) => item.id === container.locationId);
    return location?.freezerId === freezerId;
  });
  const available = result?.remainingQuantity ?? result?.initialQuantity ?? null;
  const numericCustom = Number(customQuantity.replace(',', '.'));
  const usedQuantity =
    quantityMode === 'one'
      ? 1
      : quantityMode === 'custom' && Number.isFinite(numericCustom)
        ? numericCustom
        : available;
  const remainingAfter =
    available == null || usedQuantity == null ? null : Math.max(0, available - usedQuantity);
  const canValidate =
    result != null &&
    (quantityMode === 'all' ||
      (result.unit != null &&
        usedQuantity != null &&
        usedQuantity > 0 &&
        available != null &&
        usedQuantity <= available));

  const reset = () => {
    setSerial('');
    setSelectedContainerId('');
    setQuantityMode('all');
    setCustomQuantity('');
    setSecondaryOpen(false);
    setFreezerId('');
  };

  const finish = async (event: FormEvent) => {
    event.preventDefault();
    if (result == null) return;
    const mutation = await performMutation(
      `quick-exit:${result.id}`,
      () =>
        quantityMode === 'all' || usedQuantity === available
          ? service.consumeAll(result)
          : service.consumePart(result, {
              mode: 'used',
              value: usedQuantity!,
              unit: result.unit!,
              replacementLocationId: result.locationId,
            }),
    );
    if (mutation != null) reset();
  };

  return (
    <section className="quick-exit" aria-labelledby="quick-exit-title" data-tour-id="quick-exit">
      <div className="quick-exit__intro">
        <span className="eyebrow">{t('quick.eyebrow')}</span>
        <h1 id="quick-exit-title">{t('quick.title')}</h1>
        <p>{t('quick.description')}</p>
      </div>
      <div className="serial-form">
        <label htmlFor="quick-serial" className="visually-hidden">{t('quick.numberLabel')}</label>
        <span aria-hidden="true">{t('common.number', { number: '' })}</span>
        <input
          id="quick-serial"
          autoFocus
          value={serial}
          onChange={(event) => {
            setSerial(event.target.value.replace(/\D/g, '').slice(0, 9));
            setSelectedContainerId('');
            setQuantityMode('all');
            setCustomQuantity('');
          }}
          inputMode="numeric"
          pattern="[0-9]*"
          enterKeyHint="next"
          autoComplete="off"
          placeholder="27"
          aria-describedby="quick-help"
        />
      </div>
      <span id="quick-help" className="visually-hidden">{t('quick.searchHelp')}</span>

      {serial !== '' && result == null ? (
        <div className="quick-result quick-result--missing" role="status">
          <Icon name="warning" />
          <div>
            <strong>{t('quick.unknownNumber')}</strong>
            <p>{t('quick.unknownNumberHelp')}</p>
          </div>
        </div>
      ) : null}

      {result != null ? (
        <form className="quick-result" aria-live="polite" onSubmit={(event) => void finish(event)}>
          <div className="quick-result__summary">
            {hasVisibleNumber(result) ? (
              <span className="serial-badge">{result.serialNumber}</span>
            ) : null}
            <div>
              <strong>{containerDisplayName(snapshot, result, locale)}</strong>
              <span>{formatQuantity(result, locale)}</span>
              <span>{locationLabel(snapshot, result.locationId, locale)}</span>
            </div>
          </div>
          <fieldset className="quantity-choice">
            <legend>{t('quick.quantityOut')}</legend>
            <div className="segmented">
              <button type="button" className={quantityMode === 'all' ? 'is-active' : ''} onClick={() => setQuantityMode('all')}>{t('quick.all')}</button>
              {result.unit != null && available != null && available >= 1 ? (
                <button type="button" className={quantityMode === 'one' ? 'is-active' : ''} onClick={() => setQuantityMode('one')}>{t('quick.onePortion')}</button>
              ) : null}
              {result.unit != null && available != null ? (
                <button type="button" className={quantityMode === 'custom' ? 'is-active' : ''} onClick={() => setQuantityMode('custom')}>{t('quick.customQuantity')}</button>
              ) : null}
            </div>
          </fieldset>
          {quantityMode === 'custom' ? (
            <Field
              label={t('quick.quantityOutUnit', { unit: result.unit == null ? '' : unitLabel(result.unit, locale, 1) })}
              value={customQuantity}
              onChange={(event) => setCustomQuantity(event.target.value)}
              inputMode="decimal"
              min="0"
              step="any"
              required
            />
          ) : null}
          <p className="quantity-preview">
            {quantityMode === 'all'
              ? hasVisibleNumber(result)
                ? t('quick.exitAllFreezer')
                : t('quick.exitAllFridge')
              : remainingAfter == null
                ? t('quick.remainingUnavailable')
                : t('quick.remaining', { quantity: i18n.formatNumber(remainingAfter), unit: result.unit == null ? '' : unitLabel(result.unit, locale, remainingAfter) })}
          </p>
          <div className="quick-actions">
            <Button type="submit" disabled={!canValidate || busyKeys.has(`quick-exit:${result.id}`)} icon={<Icon name="check" />}>
              {t('quick.confirm')}
            </Button>
            <Button type="button" variant="ghost" onClick={reset}>{t('common.cancel')}</Button>
          </div>
        </form>
      ) : null}

      <div className="quick-fallback">
        <button type="button" onClick={() => setSecondaryOpen((value) => !value)} aria-expanded={secondaryOpen}>
          {t('quick.unknownPath')}
        </button>
        {secondaryOpen ? (
          <div className="quick-fallback__fields">
            <SelectField label={t('equipment.appliance')} value={freezerId} onChange={(event) => {
              setFreezerId(event.target.value);
              setSerial('');
              setSelectedContainerId('');
            }}>
              <option value="">{t('common.choose')}</option>
              {snapshot.freezers.map((freezer) => <option key={freezer.id} value={freezer.id}>{snapshotDisplayLabel(snapshot, freezer.name, locale)}</option>)}
            </SelectField>
            {freezerId !== '' ? (
              <SelectField label={t('quick.foodOrContainer')} value={result?.id ?? ''} onChange={(event) => {
                const selected = activeContainers.find((container) => container.id === event.target.value);
                setSelectedContainerId(selected?.id ?? '');
                setSerial(
                  selected != null && hasVisibleNumber(selected)
                    ? String(selected.serialNumber)
                    : '',
                );
                setQuantityMode('all');
              }}>
                <option value="">{t('common.choose')}</option>
                {freezerContainers.map((container) => (
                  <option key={container.id} value={container.id}>
                    {hasVisibleNumber(container) ? `${t('common.number', { number: container.serialNumber })} — ` : ''}{containerDisplayName(snapshot, container, locale)}
                  </option>
                ))}
              </SelectField>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
};

const HomePage = ({
  snapshot,
  onAdd,
  onOpen,
  onNavigate,
  exitPrefillId,
}: {
  snapshot: InventorySnapshot;
  onAdd: () => void;
  onOpen: (modal: NonNullable<QuickModal>) => void;
  onNavigate: (view: View) => void;
  exitPrefillId: string | null;
}) => {
  const { t, tp } = useI18n();
  const present = snapshot.containers.filter((container) =>
    ['present', 'partial'].includes(container.status),
  );
  const priorities = sortByPriority(present, localToday()).filter((container) =>
    ['incident', 'overdue', 'today', 'urgent', 'soon'].includes(calculatePriority(container, localToday()).level),
  );
  const attentionCount =
    priorities.length +
    snapshot.containers.filter((container) => container.status === 'draft' || container.auditFlag !== 'ok').length;
  const fridgeIds = new Set(
    snapshot.freezers
      .filter((appliance) => appliance.equipmentKind === 'fridge' && appliance.archivedAt == null)
      .map((appliance) => appliance.id),
  );
  const fridgeLocationIds = new Set(
    snapshot.locations
      .filter((location) => fridgeIds.has(location.freezerId))
      .map((location) => location.id),
  );
  const fridgeCount = present.filter((container) => fridgeLocationIds.has(container.locationId)).length;
  return (
    <div className="page page--home">
      <QuickExit key={exitPrefillId ?? 'manual'} snapshot={snapshot} prefilledContainerId={exitPrefillId} />
      <SectionHeader
        eyebrow={t('home.eyebrow')}
        title={t('home.title')}
        description={t('home.description')}
        action={<Button onClick={onAdd} icon={<Icon name="plus" />}>{t('home.add')}</Button>}
      />
      <section className="dashboard-summary" aria-label={t('home.shortcuts')} data-tour-id="stock-overview">
        <button type="button" className="dashboard-summary__card" onClick={() => onNavigate('fridge')}>
          <span className="dashboard-summary__icon"><Icon name="snow" /></span>
          <span><strong>{fridgeCount}</strong><small>{tp('home.fridgeItems', fridgeCount)}</small></span>
          <Icon name="arrow" />
        </button>
        <button type="button" className="dashboard-summary__card" onClick={() => onNavigate('inventory')}>
          <span className="dashboard-summary__icon"><Icon name="box" /></span>
          <span><strong>{present.length}</strong><small>{tp('home.present', present.length)}</small></span>
          <Icon name="arrow" />
        </button>
        <button type="button" className="dashboard-summary__card" onClick={() => onNavigate('priorities')}>
          <span className="dashboard-summary__icon dashboard-summary__icon--attention"><Icon name="clock" /></span>
          <span><strong>{attentionCount}</strong><small>{tp('home.attention', attentionCount)}</small></span>
          <Icon name="arrow" />
        </button>
        <button type="button" className="dashboard-summary__card" onClick={() => onNavigate('more')}>
          <span className="dashboard-summary__icon"><Icon name="download" /></span>
          <span><strong>{t('home.backup')}</strong><small>{t('home.backupHelp')}</small></span>
          <Icon name="arrow" />
        </button>
      </section>
      <section className="home-grid">
        {snapshot.meta.demoSeededAt != null ? (
          <div className="notice notice--info home-demo-notice">
            <Icon name="info" />
            <p><strong>{t('home.demoTitle')}</strong> {t('home.demoHelp')}</p>
            <Button variant="secondary" onClick={() => onNavigate('meals')}>
              {t('publicMeal.homeCta')}
            </Button>
          </div>
        ) : null}
        <div className="panel">
          <div className="panel__heading">
            <div>
              <span className="eyebrow">{t('home.priorityEyebrow')}</span>
              <h2>{t('home.priorityTitle')}</h2>
            </div>
            <button type="button" className="text-link" onClick={() => onNavigate('priorities')}>{t('common.viewAll')}</button>
          </div>
          {priorities.length === 0 ? (
            <EmptyState title={t('home.noUrgency')}>
              {t('home.noUrgencyHelp')}
            </EmptyState>
          ) : (
            <div className="compact-list">
              {priorities.slice(0, 3).map((container) => (
                <ContainerCard
                  key={container.id}
                  container={container}
                  snapshot={snapshot}
                  onClick={() => onOpen({ type: 'detail', containerId: container.id })}
                />
              ))}
            </div>
          )}
        </div>
        <div className="panel">
          <div className="panel__heading">
            <div>
              <span className="eyebrow">{t('home.recentEyebrow')}</span>
              <h2>{t('home.recentTitle')}</h2>
            </div>
            <button type="button" className="text-link" onClick={() => onNavigate('inventory')}>{t('home.allStock')}</button>
          </div>
          {present.length === 0 ? (
            <EmptyState title={t('home.empty')}>
              {t('home.emptyHelp')}
            </EmptyState>
          ) : (
            <div className="compact-list">
              {present.slice(0, 3).map((container) => (
                <ContainerCard
                  key={container.id}
                  container={container}
                  snapshot={snapshot}
                  onClick={() => onOpen({ type: 'detail', containerId: container.id })}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const FridgePage = ({
  snapshot,
  onAdd,
  onOpen,
  onNavigate,
}: {
  snapshot: InventorySnapshot;
  onAdd: () => void;
  onOpen: (modal: NonNullable<QuickModal>) => void;
  onNavigate: (view: View) => void;
}) => {
  const { locale, t, tp } = useI18n();
  const { service, performMutation } = useApp();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'priority' | 'opened'>('all');
  const fridgeIds = new Set(
    snapshot.freezers
      .filter((appliance) => appliance.equipmentKind === 'fridge' && appliance.archivedAt == null)
      .map((appliance) => appliance.id),
  );
  const fridgeLocationIds = new Set(
    snapshot.locations
      .filter((location) => fridgeIds.has(location.freezerId))
      .map((location) => location.id),
  );
  const activeFridgeLocationIds = new Set(
    snapshot.locations
      .filter((location) => location.archivedAt == null && fridgeIds.has(location.freezerId))
      .map((location) => location.id),
  );
  const fridgeReady = activeFridgeLocationIds.size > 0;
  const active = sortByPriority(
    snapshot.containers.filter(
      (container) =>
        container.trackingMode === 'internal-only' &&
        fridgeLocationIds.has(container.locationId) &&
        ['present', 'partial'].includes(container.status),
    ),
    localToday(),
  );
  const normalizedQuery = query.trim().toLocaleLowerCase(locale);
  const priorityItems = active.filter((container) =>
    ['incident', 'overdue', 'today', 'urgent', 'soon'].includes(
      calculatePriority(container, localToday()).level,
    ),
  );
  const incidents = active.filter((container) => container.incidentStatus === 'unresolved');
  const visible = active.filter((container) => {
    if (
      normalizedQuery &&
      !containerDisplayName(snapshot, container, locale).toLocaleLowerCase(locale).includes(normalizedQuery) &&
      !container.barcode?.includes(normalizedQuery)
    ) {
      return false;
    }
    if (filter === 'priority') return priorityItems.some((item) => item.id === container.id);
    if (filter === 'opened') return container.openedOn != null;
    return true;
  });

  return (
    <div className="page page--fridge">
      <SectionHeader
        eyebrow={t('fridge.eyebrow')}
        title={t('fridge.title')}
        description={t('fridge.description')}
        action={fridgeReady
          ? <Button onClick={onAdd} icon={<Icon name="plus" />}>{t('fridge.add')}</Button>
          : <Button onClick={() => onNavigate('more')} icon={<Icon name="snow" />}>{t('fridge.configure')}</Button>}
      />
      <div className="fridge-mode-banner">
        <Icon name="snow" size={30} />
        <div>
          <strong>{t('fridge.distinctTitle')}</strong>
          <p>{t('fridge.distinctHelp')}</p>
        </div>
      </div>
      <section className="priority-summary" aria-label={t('fridge.summary')}>
        <article><strong>{active.length}</strong><span>{tp('fridge.items', active.length)}</span></article>
        <article><strong>{priorityItems.length}</strong><span>{tp('fridge.priority', priorityItems.length)}</span></article>
        <article><strong>{active.filter((container) => container.openedOn != null).length}</strong><span>{tp('fridge.opened', active.filter((container) => container.openedOn != null).length)}</span></article>
      </section>
      {incidents.length > 0 ? (
        <button type="button" className="notice notice--danger fridge-incident-link" onClick={() => onNavigate('more')}>
          <Icon name="warning" />
          <span><strong>{tp('fridge.incidents', incidents.length)}</strong><small>{t('fridge.openTemperatureQueue')}</small></span>
          <Icon name="arrow" />
        </button>
      ) : null}
      <section className="filter-panel" aria-label={t('fridge.searchFilters')}>
        <div className="search-field">
          <Icon name="search" />
          <label className="visually-hidden" htmlFor="fridge-search">{t('fridge.search')}</label>
          <input id="fridge-search" type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t('fridge.searchPlaceholder')} />
        </div>
        <div className="segmented" role="group" aria-label={t('fridge.filter')}>
          <button type="button" className={filter === 'all' ? 'is-active' : ''} onClick={() => setFilter('all')}>{t('fridge.filterAll')}</button>
          <button type="button" className={filter === 'priority' ? 'is-active' : ''} onClick={() => setFilter('priority')}>{t('fridge.filterPriority')}</button>
          <button type="button" className={filter === 'opened' ? 'is-active' : ''} onClick={() => setFilter('opened')}>{t('fridge.filterOpened')}</button>
        </div>
      </section>
      {fridgeIds.size === 0 ? (
        <EmptyState icon="snow" title={t('fridge.noAppliance')}>
          {t('fridge.noApplianceHelp')}
        </EmptyState>
      ) : activeFridgeLocationIds.size === 0 && visible.length === 0 ? (
        <EmptyState icon="snow" title={t('fridge.noLocation')}>
          {t('fridge.noLocationHelp')}
        </EmptyState>
      ) : visible.length === 0 ? (
        <EmptyState icon="search" title={t('fridge.noFood')}>
          {t('fridge.noFoodHelp')}
        </EmptyState>
      ) : (
        <div className="inventory-grid fridge-inventory-grid">
          {visible.map((container) => (
            <ContainerCard
              key={container.id}
              container={container}
              snapshot={snapshot}
              onClick={() => onOpen({ type: 'detail', containerId: container.id })}
              onExit={() => void performMutation(
                `fridge-consume:${container.id}`,
                () => service.consumeAll(container),
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const InventoryPage = ({
  snapshot,
  onAdd,
  onOpen,
  onExit,
}: {
  snapshot: InventorySnapshot;
  onAdd: () => void;
  onOpen: (modal: NonNullable<QuickModal>) => void;
  onExit: (container: FoodContainer) => void;
}) => {
  const { locale, t, tp } = useI18n();
  const { service, performMutation } = useApp();
  const [query, setQuery] = useState('');
  const [freezerId, setFreezerId] = useState('all');
  const [locationId, setLocationId] = useState('all');
  const [categoryId, setCategoryId] = useState('all');
  const [priority, setPriority] = useState<PriorityLevel | 'all'>('all');
  const [status, setStatus] = useState<'active' | 'history'>('active');
  const normalizedQuery = query.trim().toLocaleLowerCase(locale);

  const visible = snapshot.containers
    .filter((container) => {
      const freezer = snapshot.locations.find((location) => location.id === container.locationId)?.freezerId;
      const activeMatch = status === 'active' ? isActive(container) : !isActive(container);
      const searchMatch =
        !normalizedQuery ||
        containerDisplayName(snapshot, container, locale).toLocaleLowerCase(locale).includes(normalizedQuery) ||
        (container.serialNumber != null && String(container.serialNumber).includes(normalizedQuery)) ||
        (container.barcode != null && container.barcode.includes(normalizedQuery));
      const priorityMatch =
        priority === 'all' || calculatePriority(container, localToday()).level === priority;
      return (
        activeMatch &&
        searchMatch &&
        (freezerId === 'all' || freezer === freezerId) &&
        (locationId === 'all' || container.locationId === locationId) &&
        (categoryId === 'all' || container.categoryId === categoryId) &&
        priorityMatch
      );
    })
    .sort(
      (left, right) =>
        (right.serialNumber ?? -1) - (left.serialNumber ?? -1) ||
        right.createdAt.localeCompare(left.createdAt),
    );

  return (
    <div className="page">
      <SectionHeader
        eyebrow={t('inventory.eyebrow')}
        title={t('inventory.title')}
        description={tp('inventory.active', snapshot.containers.filter(isActive).length)}
        action={<Button onClick={onAdd} icon={<Icon name="plus" />}>{t('inventory.add')}</Button>}
      />
      <section className="filter-panel" aria-label={t('inventory.searchFilters')}>
        <div className="filter-panel__primary">
          <div className="search-field">
            <Icon name="search" />
            <label className="visually-hidden" htmlFor="inventory-search">{t('inventory.search')}</label>
            <input
              id="inventory-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('inventory.searchPlaceholder')}
            />
          </div>
          <div className="segmented" role="group" aria-label={t('inventory.state')}>
            <button type="button" className={status === 'active' ? 'is-active' : ''} onClick={() => setStatus('active')}>{t('inventory.present')}</button>
            <button type="button" className={status === 'history' ? 'is-active' : ''} onClick={() => setStatus('history')}>{t('inventory.history')}</button>
          </div>
        </div>
        <div className="filter-grid">
          <SelectField label={t('equipment.appliance')} value={freezerId} onChange={(event) => {
            setFreezerId(event.target.value);
            setLocationId('all');
          }}>
            <option value="all">{t('common.allMasculine')}</option>
            {snapshot.freezers.map((freezer) => <option key={freezer.id} value={freezer.id}>{snapshotDisplayLabel(snapshot, freezer.name, locale)}</option>)}
          </SelectField>
          <SelectField label={t('location.location')} value={locationId} onChange={(event) => setLocationId(event.target.value)}>
            <option value="all">{t('common.allMasculine')}</option>
            {snapshot.locations
              .filter((location) => freezerId === 'all' || location.freezerId === freezerId)
              .map((location) => <option key={location.id} value={location.id}>{locationLabel(snapshot, location.id, locale)}</option>)}
          </SelectField>
          <SelectField label={t('detail.category')} value={categoryId} onChange={(event) => setCategoryId(event.target.value)}>
            <option value="all">{t('common.allFeminine')}</option>
            {FOOD_CATEGORIES.map((category) => <option key={category.id} value={category.id}>{categoryLabel(category.id, locale)}</option>)}
          </SelectField>
          <SelectField label={t('inventory.priority')} value={priority} onChange={(event) => setPriority(event.target.value as PriorityLevel | 'all')}>
            <option value="all">{t('common.allFeminine')}</option>
            {(['incident', 'overdue', 'today', 'urgent', 'soon', 'normal', 'none'] as const).map((value) => <option key={value} value={value}>{t(`priority.${value}` as MessageKey)}</option>)}
          </SelectField>
        </div>
      </section>
      <p className="results-count" aria-live="polite">{tp('inventory.results', visible.length)}</p>
      {visible.length === 0 ? (
        <EmptyState icon="search" title={t('inventory.noResult')}>
          {t('inventory.noResultHelp')}
        </EmptyState>
      ) : (
        <div className="inventory-grid">
          {visible.map((container) => (
            <ContainerCard
              key={container.id}
              container={container}
              snapshot={snapshot}
              onClick={() => onOpen({ type: 'detail', containerId: container.id })}
              onExit={() => {
                if (container.trackingMode === 'internal-only') {
                  void performMutation(
                    `fridge-consume:${container.id}`,
                    () => service.consumeAll(container),
                  );
                } else {
                  onExit(container);
                }
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const PrioritiesPage = ({
  snapshot,
  onOpen,
  onOpenRecipes,
}: {
  snapshot: InventorySnapshot;
  onOpen: (modal: NonNullable<QuickModal>) => void;
  onOpenRecipes: (container: FoodContainer) => void;
}) => {
  const { service, perform } = useApp();
  const { locale, t } = useI18n();
  const active = snapshot.containers.filter(isActive);
  const sorted = sortByPriority(active, localToday());
  const priorityItems = sorted.filter((container) => {
    const result = calculatePriority(container, localToday());
    return ['incident', 'overdue', 'today', 'urgent', 'soon'].includes(result.level);
  });
  const unreadRotationAlerts = active.flatMap((container) =>
    container.rotationAlerts
      .filter((alert) => alert.state === 'unread')
      .map((alert) => ({ container, alert })),
  );
  const drafts = active.filter((container) => container.status === 'draft');
  const auditFlags = active.filter((container) => container.auditFlag !== 'ok');
  const unreadAlertContainerIds = new Set(
    unreadRotationAlerts.map(({ container }) => container.id),
  );

  return (
    <div className="page" data-tour-id="priorities">
      <SectionHeader
        eyebrow={t('priorities.eyebrow')}
        title={t('priorities.title')}
        description={t('priorities.description')}
      />
      <div className="safety-banner">
        <Icon name="info" />
        <p>{t('priorities.safety')}</p>
      </div>
      <div className="priority-summary">
        <article><strong>{priorityItems.length}</strong><span>{t('priorities.rotationIncidents')}</span></article>
        <article><strong>{drafts.length}</strong><span>{t('priorities.numbers')}</span></article>
        <article><strong>{auditFlags.length}</strong><span>{t('priorities.presences')}</span></article>
      </div>
      {priorityItems.length === 0 && drafts.length === 0 && auditFlags.length === 0 ? (
        <EmptyState icon="check" title={t('priorities.empty')}>
          {t('priorities.emptyHelp')}
        </EmptyState>
      ) : (
        <>
          {unreadRotationAlerts.length > 0 ? (
            <section className="page-section">
              <h2>{t('priorities.thresholds')}</h2>
              <div className="alert-list">
                {unreadRotationAlerts.map(({ container, alert }) => (
                  <article key={`${container.id}:${alert.threshold}`} className="notice notice--warning">
                    <div>
                      <strong>{t('priorities.threshold', { reference: containerReference(container, locale), threshold: alert.threshold })}</strong>
                      <p>{t('priorities.bestUse', { name: containerDisplayName(snapshot, container, locale), date: formatDate(container.qualityHorizonOn, locale) })}</p>
                    </div>
                    <div className="notice-actions">
                      <Button variant="secondary" onClick={() => void perform(
                        `rotation-read:${container.id}:${alert.threshold}`,
                        () => service.setRotationAlertState(container.id, alert.threshold, 'read'),
                        { success: t('priorities.readSuccess') },
                      )}>{t('priorities.markRead')}</Button>
                      {container.incidentStatus !== 'unresolved' ? (
                        <RecipeIdeasCta
                          container={container}
                          locale={locale}
                          onLocalRecipes={onOpenRecipes}
                        />
                      ) : null}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ) : null}
          {priorityItems.length > 0 ? (
            <section className="page-section">
              <h2>{t('priorities.rotationSection')}</h2>
              <div className="inventory-grid">
                {priorityItems.map((container) => {
                  const priority = calculatePriority(container, localToday());
                  const canSuggestRecipes =
                    priority.level !== 'incident' &&
                    !unreadAlertContainerIds.has(container.id);
                  return (
                    <div className="priority-product" key={container.id}>
                      <ContainerCard container={container} snapshot={snapshot} onClick={() => onOpen({ type: 'detail', containerId: container.id })} />
                      {canSuggestRecipes ? (
                        <RecipeIdeasCta
                          container={container}
                          locale={locale}
                          onLocalRecipes={onOpenRecipes}
                        />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </section>
          ) : null}
          {drafts.length > 0 ? (
            <section className="page-section">
              <h2>{t('priorities.draftsSection')}</h2>
              <div className="inventory-grid">
                {drafts.map((container) => (
                  <ContainerCard key={container.id} container={container} snapshot={snapshot} onClick={() => onOpen({ type: 'detail', containerId: container.id })} />
                ))}
              </div>
            </section>
          ) : null}
          {auditFlags.length > 0 ? (
            <section className="page-section">
              <h2>{t('priorities.auditSection')}</h2>
              <div className="inventory-grid">
                {auditFlags.map((container) => (
                  <ContainerCard key={container.id} container={container} snapshot={snapshot} onClick={() => onOpen({ type: 'detail', containerId: container.id })} />
                ))}
              </div>
            </section>
          ) : null}
        </>
      )}
    </div>
  );
};

const MealsPage = ({
  snapshot,
  onOpen,
  focusContainerId,
  recipeReturn,
  onRecipeReturnHandled,
}: {
  snapshot: InventorySnapshot;
  onOpen: (modal: NonNullable<QuickModal>) => void;
  focusContainerId: string | null;
  recipeReturn: RecipeReturn | null;
  onRecipeReturnHandled: () => void;
}) => {
  const { service, perform, busyKeys, runtime } = useApp();
  const { locale, t, tp } = useI18n();
  const standardSuggestions = service.mealSuggestions(snapshot, MEAL_TEMPLATES);
  const returnedTemplateId = recipeReturn?.kind === 'contextual'
    ? recipeReturn.localTemplateId
    : recipeReturn?.publicRecipeId ?? null;
  const contextualSuggestions = MEAL_TEMPLATES
    .map((template) => service.mealSuggestion(snapshot, template, template.defaultPortions ?? 2))
    .filter((suggestion): suggestion is NonNullable<typeof suggestion> => suggestion != null)
    .filter((suggestion) =>
      (returnedTemplateId != null && suggestion.template.id === returnedTemplateId) ||
      (focusContainerId != null && suggestion.containers.some((item) => item.id === focusContainerId)),
    )
    .sort((left, right) => right.score - left.score);
  const suggestions = [...new Map(
    [...contextualSuggestions, ...standardSuggestions]
      .map((suggestion) => [suggestion.template.id, suggestion]),
  ).values()].slice(0, 3);
  const [portionsByRecipe, setPortionsByRecipe] = useState<Record<string, number>>({});
  const [preparation, setPreparation] = useState<{
    suggestion: NonNullable<ReturnType<typeof service.mealSuggestion>>;
    idempotencyKey: string;
  } | null>(null);
  const [lastBatch, setLastBatch] = useState<InventoryBatchMutation | null>(null);
  const returnProcessed = useRef<string | null>(null);
  const [returnNotice, setReturnNotice] = useState<MessageKey | null>(null);
  const [shoppingCheckOverrides, setShoppingCheckOverrides] = useState<Record<string, boolean>>({});
  const [handoffListId, setHandoffListId] = useState<string | null>(null);
  const [handoffTarget, setHandoffTarget] = useState<HandoffTarget>(readHandoffTarget);
  const [handoffTransferListId, setHandoffTransferListId] = useState<string | null>(null);
  const [handoffView, setHandoffView] = useState<ShoppingListHandoffView>({ stage: 'checking' });
  const handoffViewRef = useRef<ShoppingListHandoffView>(handoffView);
  const [handoffEmail, setHandoffEmail] = useState('');
  const [releaseListId, setReleaseListId] = useState<string | null>(null);
  const recipeLists = useMemo(
    () => snapshot.preferences?.recipeShoppingLists ?? [],
    [snapshot.preferences?.recipeShoppingLists],
  );
  const handoffList = handoffListId == null
    ? null
    : recipeLists.find((list) => list.id === handoffListId) ?? null;
  const releaseList = releaseListId == null
    ? null
    : recipeLists.find((list) => list.id === releaseListId) ?? null;
  const returnedTemplate = returnedTemplateId == null
    ? null
    : MEAL_TEMPLATES.find((template) => template.id === returnedTemplateId) ?? null;
  const returnedTitle = returnedTemplate == null ? null : mealTemplateTitle(returnedTemplate, locale);

  useEffect(() => {
    handoffViewRef.current = handoffView;
  }, [handoffView]);

  useEffect(() => {
    if (handoffListId == null) return;
    let active = true;
    const resume = async () => {
      await Promise.resolve();
      if (!active) return;
      const persistedReceipt = recipeLists.find((list) => list.id === handoffListId)?.handoffReceipt;
      if (persistedReceipt != null) {
        setHandoffTransferListId(handoffListId);
        setHandoffView({ stage: 'accepted', intentId: persistedReceipt.intentId });
        return;
      }
      if (
        handoffTransferListId === handoffListId &&
        ['issued', 'awaiting_target', 'wrapped', 'awaiting_receipt', 'accepted',
          'receipt_declined', 'receipt_expired', 'receipt_invalid', 'receipt_unavailable']
          .includes(handoffViewRef.current.stage)
      ) return;
      setHandoffView({ stage: 'checking' });
      const receipt = await service.retrieveArchibaldReceipt(handoffListId, runtime.mode);
      if (!active) return;
      if (receipt.state === 'accepted') {
        const refreshed = await perform(
          `archibald-handoff:receipt-persisted:${handoffListId}`,
          () => Promise.resolve(receipt),
          { silent: true },
        );
        if (!active || refreshed == null) return;
      }
      const resumed = handoffViewFromReceipt(receipt);
      if (resumed != null) {
        setHandoffTransferListId(handoffListId);
        setHandoffView(resumed);
        return;
      }
      const status = await service.archibaldHandoffStatus(runtime.mode, handoffTarget);
      if (active) setHandoffView(handoffViewFromStatus(status));
    };
    void resume().catch(() => {
      if (active) setHandoffView({ stage: 'unavailable', fallbackUrl: null });
    });
    return () => {
      active = false;
    };
  }, [handoffListId, handoffTarget, handoffTransferListId, perform, recipeLists, runtime.mode, service]);

  useEffect(() => {
    if (
      handoffListId == null || handoffTransferListId !== handoffListId ||
      (handoffView.stage !== 'wrapped' && handoffView.stage !== 'awaiting_receipt')
    ) return;
    let active = true;
    let timer: number | null = null;
    const poll = async () => {
      const result = await service.retrieveArchibaldReceipt(handoffListId, runtime.mode);
      if (!active) return;
      if (result.state === 'pending') {
        if (handoffViewRef.current.stage === 'awaiting_receipt') {
          const pendingView = handoffViewFromReceipt(result);
          if (pendingView != null) setHandoffView(pendingView);
        }
        timer = window.setTimeout(() => void poll(), result.retryAfterSeconds * 1_000);
        return;
      }
      const next = handoffViewFromReceipt(result);
      if (result.state === 'accepted') {
        await perform(
          `archibald-handoff:receipt-persisted:${handoffListId}`,
          () => Promise.resolve(result),
          { silent: true },
        );
        if (!active) return;
      }
      if (next != null) setHandoffView(next);
    };
    void poll().catch(() => {
      if (active) setHandoffView({ stage: 'receipt_unavailable' });
    });
    return () => {
      active = false;
      if (timer != null) window.clearTimeout(timer);
    };
  }, [handoffListId, handoffTransferListId, handoffView.stage, perform, runtime.mode, service]);

  useEffect(() => {
    if (
      recipeReturn == null ||
      recipeReturn.kind !== 'verified' ||
      returnProcessed.current === recipeReturn.returnToken
    ) return;
    returnProcessed.current = recipeReturn.returnToken;
    stripRecipeReturnFromAddress();
    const messages: Record<string, MessageKey> = {
      imported: 'recipe.secureReturnImported',
      already_imported: 'recipe.secureReturnAlreadyImported',
      unavailable: 'recipe.secureReturnUnavailable',
      expired: 'recipe.secureReturnExpired',
      replayed: 'recipe.secureReturnReplayed',
      invalid: 'recipe.secureReturnInvalid',
      limit_reached: 'recipe.secureReturnLimitReached',
      demo_forbidden: 'recipe.secureReturnDemoForbidden',
    };
    void perform(
      `recipe-return:${recipeReturn.returnToken}`,
      () => service.importRecipeReturn(recipeReturn, MEAL_TEMPLATES, runtime.mode),
      { silent: true },
    ).then((result) => {
      if (result != null) setReturnNotice(messages[result.state] ?? 'recipe.secureReturnInvalid');
      onRecipeReturnHandled();
    });
  }, [onRecipeReturnHandled, perform, recipeReturn, runtime.mode, service]);
  useEffect(() => {
    if (recipeReturn?.kind !== 'editorial' || returnedTemplate != null) return;
    stripRecipeReturnFromAddress();
  }, [recipeReturn, returnedTemplate]);
  const confirmEditorialReturn = async () => {
    if (
      recipeReturn == null ||
      (recipeReturn.kind !== 'editorial' && recipeReturn.kind !== 'contextual')
    ) return;
    const publicRecipeId = recipeReturn.kind === 'contextual'
      ? recipeReturn.localTemplateId
      : recipeReturn.publicRecipeId;
    if (publicRecipeId == null) return;
    const messages: Record<string, MessageKey> = {
      imported: 'recipe.editorialReturnImported',
      already_imported: 'recipe.editorialReturnAlreadyImported',
      invalid: 'recipe.editorialReturnInvalid',
      limit_reached: 'recipe.secureReturnLimitReached',
      demo_forbidden: 'recipe.secureReturnDemoForbidden',
      unavailable: 'recipe.editorialReturnInvalid',
      expired: 'recipe.editorialReturnInvalid',
      replayed: 'recipe.editorialReturnInvalid',
    };
    const result = await perform(
      `editorial-recipe-return:${publicRecipeId}:${recipeReturn.locale}`,
      () => service.importEditorialRecipeReturn(
        { publicRecipeId, locale: recipeReturn.locale },
        MEAL_TEMPLATES,
        runtime.mode,
        true,
      ),
      { silent: true },
    );
    if (result != null) setReturnNotice(messages[result.state] ?? 'recipe.editorialReturnInvalid');
    stripRecipeReturnFromAddress();
    onRecipeReturnHandled();
  };
  const confirmPreparation = async (lines: ConfirmedRecipeLine[]) => {
    if (preparation == null) return;
    const result = await perform(
      preparation.idempotencyKey,
      () => service.consumeRecipe(
        preparation.suggestion.template.title,
        lines,
        preparation.idempotencyKey,
      ),
      { success: t('meals.deducted') },
    );
    if (result != null) {
      setLastBatch(result);
      setPreparation(null);
    }
  };
  const undoLastRecipe = async () => {
    if (lastBatch == null) return;
    const result = await perform(
      `undo-recipe:${lastBatch.idempotencyKey}`,
      () => service.undoRecipe(lastBatch),
      { success: t('meals.restored') },
    );
    if (result != null) setLastBatch(null);
  };
  const toggleShoppingItem = async (
    list: RecipeShoppingList,
    itemId: string,
    checked: boolean,
  ) => {
    const key = `shopping-item:${list.id}:${itemId}`;
    setShoppingCheckOverrides((current) => ({ ...current, [key]: checked }));
    await perform(
      key,
      () => service.setRecipeShoppingItemChecked(list.id, itemId, checked),
      { silent: true },
    );
    setShoppingCheckOverrides((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  };
  const releaseShoppingList = async () => {
    if (releaseList == null) return;
    const removedId = await perform(
      `shopping-list-release:${releaseList.id}`,
      () => service.releaseRecipeShoppingList(releaseList.id),
      { success: t('shopping.released') },
    );
    if (removedId == null) return;
    if (handoffListId === removedId) setHandoffListId(null);
    if (handoffTransferListId === removedId) setHandoffTransferListId(null);
    setReleaseListId(null);
  };
  const prepareHandoff = async () => {
    if (handoffList == null) return;
    const result = await perform(
      `archibald-handoff:${handoffList.id}`,
      () => service.prepareArchibaldHandoff(handoffList.id, runtime.mode, handoffTarget),
      { silent: true },
    );
    if (result == null) return;
    if (result.state === 'ready') {
      setHandoffTransferListId(handoffList.id);
      setHandoffView({
        stage: 'issued',
        targetUrl: result.url,
        intentId: result.intentId,
        expiresAt: result.expiresAt,
      });
      return;
    }
    setHandoffView({
      stage: result.state === 'demo_forbidden' ? 'unavailable' : result.state,
      fallbackUrl: result.fallbackUrl,
    });
  };
  const connectHandoff = async () => {
    const result = await perform(
      'archibald-handoff:session',
      () => handoffView.stage === 'disconnected'
        ? service.connectArchibaldHandoff(handoffEmail, runtime.mode)
        : service.archibaldHandoffStatus(runtime.mode, handoffTarget),
      { silent: true },
    );
    if (result != null) setHandoffView(handoffViewFromStatus(result));
  };
  const disconnectHandoff = async () => {
    const result = await perform(
      'archibald-handoff:session',
      () => service.disconnectArchibaldHandoff(),
      { silent: true },
    );
    if (result === null) return;
    setHandoffTransferListId(null);
    setHandoffView({ stage: 'disconnected' });
  };
  const completeHandoffKeyExchange = async (waitForTarget: boolean) => {
    if (
      handoffView.stage !== 'issued' && handoffView.stage !== 'awaiting_target' &&
      handoffView.stage !== 'wrapped'
    ) return;
    const transfer = handoffView;
    if (transfer.stage !== 'wrapped') setHandoffView({ ...transfer, stage: 'awaiting_target' });
    const result = await perform(
      'archibald-handoff:key-exchange',
      async () => {
        const attempts = waitForTarget ? 20 : 1;
        for (let attempt = 0; attempt < attempts; attempt += 1) {
          const current = await service.completeArchibaldKeyExchange(runtime.mode);
          if (current.state !== 'awaiting_target') return current;
          if (attempt + 1 < attempts) {
            await new Promise((resolve) => window.setTimeout(resolve, 2_000));
          }
        }
        return { state: 'awaiting_target' as const };
      },
      { silent: true },
    );
    if (result == null) return;
    if (result.state === 'wrapped') {
      setHandoffView({ ...transfer, stage: 'wrapped' });
    } else if (result.state === 'awaiting_target') {
      setHandoffView({ ...transfer, stage: 'awaiting_target' });
    } else if (result.state === 'expired') {
      setHandoffView({ stage: 'receipt_expired' });
    } else {
      setHandoffView({ stage: 'unavailable', fallbackUrl: null });
    }
  };
  const retryHandoffReceipt = async () => {
    if (handoffListId == null) return;
    const result = await perform(
      'archibald-handoff:receipt',
      () => service.retrieveArchibaldReceipt(handoffListId, runtime.mode),
      { silent: true },
    );
    if (result == null) return;
    if (result.state === 'pending' && handoffView.stage === 'wrapped') {
      return;
    }
    setHandoffView(handoffViewFromReceipt(result) ?? { stage: 'receipt_invalid' });
  };
  if (runtime.mode === 'demo') {
    const contextualReturn = recipeReturn?.kind === 'contextual' ? recipeReturn : null;
    return (
      <div className="page page--public-meals" data-tour-id="meal-ideas">
        <PublicMealDemo
          recipeReturn={contextualReturn}
          appHref={contextualReturn == null
            ? '/'
            : buildContextualRecipePath(contextualReturn, 'app')}
        />
      </div>
    );
  }
  return (
    <div className="page">
      <SectionHeader
        eyebrow={t('meals.eyebrow')}
        title={t('meals.title')}
        description={t('meals.description')}
      />
      <div className="safety-banner">
        <Icon name="warning" />
        <p>{t('meals.safety')}</p>
      </div>
      {recipeReturn != null || returnNotice != null ? (
        <div className="notice notice--info recipe-return-notice" role="status">
          <Icon name="arrow" />
          <div>
            <p>{returnNotice != null
              ? t(returnNotice)
              : recipeReturn?.kind === 'contextual'
                ? returnedTitle == null
                  ? t('recipe.contextualReturnFallback', {
                    ingredient: recipeReturn.ingredientId.replaceAll('-', ' '),
                    recipe: recipeReturn.recipeId.replaceAll('-', ' '),
                  })
                  : t('recipe.contextualReturnProposal', {
                    ingredient: recipeReturn.ingredientId.replaceAll('-', ' '),
                    recipe: recipeReturn.recipeId.replaceAll('-', ' '),
                    template: returnedTitle,
                  })
                : recipeReturn?.kind === 'editorial'
                ? returnedTitle == null
                  ? t('recipe.editorialReturnInvalid')
                  : t('recipe.editorialReturnProposal', { recipe: returnedTitle })
                : returnedTitle == null
                  ? t('recipe.returned')
                  : t('recipe.returnedWithTitle', { recipe: returnedTitle })}</p>
            {(recipeReturn?.kind === 'editorial' || recipeReturn?.kind === 'contextual') && returnedTitle != null ? (
              <Button
                onClick={() => void confirmEditorialReturn()}
                disabled={busyKeys.has(`editorial-recipe-return:${returnedTemplateId}:${recipeReturn.locale}`)}
                icon={<Icon name="check" />}
              >
                {t('recipe.editorialReturnConfirm')}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
      {lastBatch != null ? (
        <div className="notice notice--success recipe-undo-banner" role="status">
          <Icon name="check" />
          <p><strong>{t('meals.deductionTitle')}</strong> {tp('meals.updated', lastBatch.mutations.length)}</p>
          <Button variant="ghost" onClick={() => void undoLastRecipe()} disabled={busyKeys.has(`undo-recipe:${lastBatch.idempotencyKey}`)} icon={<Icon name="undo" />}>
            {t('meals.undo')}
          </Button>
        </div>
      ) : null}
      {suggestions.length === 0 ? (
        <EmptyState icon="meal" title={t('meals.empty')}>
          {t('meals.emptyHelp')}
        </EmptyState>
      ) : (
        <div className="meal-grid">
          {suggestions.map((baseSuggestion) => {
            const portions = portionsByRecipe[baseSuggestion.template.id] ?? baseSuggestion.portions;
            const suggestion = service.mealSuggestion(snapshot, baseSuggestion.template, portions) ?? baseSuggestion;
            const presentIngredients = mealPresentIngredients(suggestion, locale, snapshot.meta.demoSeededAt != null);
            const missingIngredients = mealMissingIngredients(
              suggestion,
              locale,
              t('meals.quantityToCheck'),
              (quantity, unit) => t('meals.missingQuantity', { quantity: new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }).format(quantity), unit: unitLabel(unit as QuantityUnit, locale, quantity) }),
            );
            return (
            <article className="meal-card" key={suggestion.template.id}>
              <div>
                <span className="eyebrow">{t('meals.localSuggestion')}</span>
                <h2>{mealTemplateTitle(suggestion.template, locale)}</h2>
                <p>{mealTemplateDescription(suggestion.template, locale)}</p>
              </div>
              <label className="recipe-portions">
                <span>{t('meals.portions')}</span>
                <select value={suggestion.portions} onChange={(event) => setPortionsByRecipe((current) => ({
                  ...current,
                  [suggestion.template.id]: Number(event.target.value),
                }))}>
                  {[1, 2, 3, 4, 5, 6, 8, 10, 12].map((value) => <option key={value} value={value}>{value}</option>)}
                </select>
              </label>
              <div>
                <h3>{t('meals.presentStock')}</h3>
                <ul className="recipe-availability recipe-availability--present">
                  {presentIngredients.map((ingredient) => <li key={ingredient}>{ingredient}</li>)}
                </ul>
                <h3>{t('meals.suggestedFoods')}</h3>
                <ul className="meal-containers">
                  {suggestion.containers.map((container) => (
                    <li key={container.id}>
                      <button type="button" onClick={() => onOpen({ type: 'detail', containerId: container.id })}>
                        {hasVisibleNumber(container) ? (
                          <span className="serial-badge">{container.serialNumber}</span>
                        ) : null}
                        <span><strong>{containerDisplayName(snapshot, container, locale)}</strong><small>{locationLabel(snapshot, container.locationId, locale)}</small></span>
                        <Icon name="arrow" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="meal-missing">
                <strong>{t('meals.missing')}</strong>
                <p>{missingIngredients.length > 0 ? missingIngredients.join(t('common.separator')) : t('meals.nothingMissing')}</p>
              </div>
              <p className="microcopy">
                {t('meals.planningHelp')}
              </p>
              <Button onClick={() => setPreparation({
                suggestion,
                idempotencyKey: `recipe-consumption:${crypto.randomUUID()}`,
              })} icon={<Icon name="meal" />}>
                {t('recipe.cookedAction')}
              </Button>
            </article>
          );})}
        </div>
      )}
      {preparation != null ? (
        <MealPreparationModal
          suggestion={preparation.suggestion}
          snapshot={snapshot}
          busy={busyKeys.has(preparation.idempotencyKey)}
          onClose={() => setPreparation(null)}
          onConfirm={confirmPreparation}
        />
      ) : null}
      {recipeLists.length > 0 ? (
        <section className="page-section shopping-lists-section">
          <SectionHeader
            eyebrow={t('shopping.eyebrow')}
            title={t('shopping.title')}
            description={t('shopping.description')}
          />
          <div className="shopping-list-grid">
            {recipeLists.map((list) => (
              <article className="shopping-list-card" key={list.id}>
                <div>
                  <h3>{mealTemplateTitle({ id: list.publicRecipeId, title: list.recipeTitle }, locale)}</h3>
                  <small>{tp('meal.portionsDeduction', list.portions)}</small>
                </div>
                {list.items.length === 0 ? <p>{t('shopping.empty')}</p> : (
                  <ul className="shopping-list-items">
                    {list.items.map((item) => (
                      <li key={item.id}>
                        <label>
                          {(() => {
                            const shoppingItemKey = `shopping-item:${list.id}:${item.id}`;
                            return (
                          <input
                            type="checkbox"
                            checked={shoppingCheckOverrides[shoppingItemKey] ?? item.checked}
                            disabled={busyKeys.has(shoppingItemKey)}
                            aria-busy={busyKeys.has(shoppingItemKey)}
                            data-persisted-checked={item.checked}
                            onChange={(event) => void toggleShoppingItem(list, item.id, event.target.checked)}
                          />
                            );
                          })()}
                          <span>{recipeShoppingItemLabel(item, locale)}</span>
                        </label>
                        <small>{item.requiredQuantity == null || item.unit == null
                          ? t('shopping.quantityToCheck')
                          : `${new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }).format(item.requiredQuantity)} ${unitLabel(item.unit, locale, item.requiredQuantity)}`}</small>
                      </li>
                    ))}
                  </ul>
                )}
                {list.handoffReceipt == null ? (
                  <p className="microcopy">{t('shopping.sourceRetained')}</p>
                ) : (
                  <div className="notice notice--success" role="status">
                    <Icon name="check" />
                    <p>{t('shopping.accepted')}</p>
                  </div>
                )}
                {runtime.mode === 'app' && service.handoffAvailable() ? (
                  <OfficialHandoffReceipt load={intentId => service.exportArchibaldReceipt(list.id, runtime.mode, intentId)} />
                ) : null}
                {list.handoffReceipt != null ? (
                  <Button
                    variant="ghost"
                    disabled={busyKeys.has(`shopping-list-release:${list.id}`)}
                    onClick={() => setReleaseListId(list.id)}
                    icon={<Icon name="trash" />}
                  >
                    {t('shopping.release')}
                  </Button>
                ) : service.handoffAvailable() ? (
                  <div className="button-stack">
                    {(['archibald', 'paniprix'] as const).map((target) => (
                      <Button
                        key={target}
                        variant="secondary"
                        disabled={list.items.length === 0}
                        onClick={() => {
                          rememberHandoffTarget(target);
                          setHandoffTarget(target);
                          setHandoffTransferListId(null);
                          setHandoffView({ stage: 'checking' });
                          setHandoffListId(list.id);
                        }}
                        icon={<Icon name="arrow" />}
                      >
                        {t(target === 'archibald' ? 'shopping.sendArchibald' : 'shopping.sendPaniPrix')}
                      </Button>
                    ))}
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}
      {handoffList != null ? (
        <ShoppingListHandoffModal
          list={handoffList}
          target={handoffTarget}
          busy={busyKeys.has(`archibald-handoff:${handoffList.id}`) ||
            busyKeys.has('archibald-handoff:session') ||
            busyKeys.has('archibald-handoff:key-exchange') ||
            busyKeys.has('archibald-handoff:receipt')}
          view={handoffView}
          email={handoffEmail}
          onClose={() => setHandoffListId(null)}
          onConnect={connectHandoff}
          onDisconnect={disconnectHandoff}
          onEmailChange={setHandoffEmail}
          onPrepare={prepareHandoff}
          onRetryKeyExchange={() => completeHandoffKeyExchange(false)}
          onRetryReceipt={retryHandoffReceipt}
          onTargetOpened={() => completeHandoffKeyExchange(true)}
        />
      ) : null}
      {releaseList?.handoffReceipt != null ? (
        <Modal
          title={t('shopping.releaseTitle')}
          description={t('shopping.releaseDescription')}
          onClose={() => setReleaseListId(null)}
        >
          <div className="form-stack">
            <div className="notice notice--success">
              <Icon name="check" />
              <p>{t('shopping.accepted')}</p>
            </div>
            <div className="modal-actions">
              <Button
                variant="ghost"
                disabled={busyKeys.has(`shopping-list-release:${releaseList.id}`)}
                onClick={() => setReleaseListId(null)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="danger"
                disabled={busyKeys.has(`shopping-list-release:${releaseList.id}`)}
                onClick={() => void releaseShoppingList()}
                icon={<Icon name="trash" />}
              >
                {t('shopping.releaseConfirm')}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
};

const AuditPanel = ({ snapshot }: { snapshot: InventorySnapshot }) => {
  const { locale, t, tp } = useI18n();
  const { service, perform, busyKeys } = useApp();
  const auditableFreezers = snapshot.freezers.filter(
    (freezer) => storagePolicyFor(freezer.equipmentKind).trackingMode === 'visible-number',
  );
  const [freezerId, setFreezerId] = useState(auditableFreezers[0]?.id ?? '');
  const availableLocations = snapshot.locations.filter((location) => location.freezerId === freezerId);
  const [locationId, setLocationId] = useState(availableLocations[0]?.id ?? '');
  const [seen, setSeen] = useState<Set<number>>(new Set());
  const [unknown, setUnknown] = useState('');
  const [record, setRecord] = useState<AuditRecord | null>(null);
  const expected = snapshot.containers
    .filter(hasVisibleNumber)
    .filter((container) => container.locationId === locationId && ['present', 'partial'].includes(container.status))
    .sort((left, right) => left.serialNumber - right.serialNumber);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const unknownSerials = unknown
      .split(/[\s,;]+/)
      .map(Number)
      .filter((value) => Number.isInteger(value) && value > 0);
    const result = await perform(
      'complete-audit',
      () => service.completeAudit({
        freezerId,
        locationId,
        seenSerials: [...seen],
        unknownSerials,
      }),
      { success: t('audit.saved') },
    );
    if (result != null) setRecord(result);
  };

  return (
    <section className="settings-card" id="audit">
      <div className="settings-card__heading">
        <span className="settings-icon"><Icon name="audit" /></span>
        <div>
          <h2>{t('audit.title')}</h2>
          <p>{t('audit.help')}</p>
        </div>
      </div>
      <form className="form-stack" onSubmit={(event) => void submit(event)}>
        <div className="form-row">
          <SelectField label={t('audit.numberedFreezer')} value={freezerId} onChange={(event) => {
            const nextFreezerId = event.target.value;
            setFreezerId(nextFreezerId);
            setLocationId(snapshot.locations.find((location) => location.freezerId === nextFreezerId)?.id ?? '');
            setSeen(new Set());
            setRecord(null);
          }}>
            {auditableFreezers.map((freezer) => <option key={freezer.id} value={freezer.id}>{snapshotDisplayLabel(snapshot, freezer.name, locale)}</option>)}
          </SelectField>
          <SelectField label={t('location.location')} value={locationId} onChange={(event) => {
            setLocationId(event.target.value);
            setSeen(new Set());
            setRecord(null);
          }}>
            {availableLocations.map((location) => <option key={location.id} value={location.id}>{locationLabel(snapshot, location.id, locale)}</option>)}
          </SelectField>
        </div>
        {expected.length === 0 ? (
          <p className="muted">{t('audit.noExpected')}</p>
        ) : (
          <fieldset className="audit-list">
            <legend>{t('audit.expected')}</legend>
            {expected.map((container) => (
              <label key={container.id}>
                <input
                  type="checkbox"
                  checked={seen.has(container.serialNumber)}
                  onChange={(event) => {
                    const next = new Set(seen);
                    if (event.target.checked) next.add(container.serialNumber);
                    else next.delete(container.serialNumber);
                    setSeen(next);
                  }}
                />
                <span className="serial-badge">{container.serialNumber}</span>
                <span><strong>{containerDisplayName(snapshot, container, locale)}</strong><small>{formatQuantity(container, locale)}</small></span>
              </label>
            ))}
          </fieldset>
        )}
        <Field
          label={t('audit.unknown')}
          value={unknown}
          onChange={(event) => setUnknown(event.target.value)}
          inputMode="numeric"
          placeholder={t('audit.example')}
          hint={t('audit.unknownHint')}
        />
        <Button type="submit" disabled={!locationId || busyKeys.has('complete-audit')} icon={<Icon name="check" />}>
          {t('audit.submit')}
        </Button>
      </form>
      {record != null ? (
        <div className="audit-report" role="status">
          <strong>{t('audit.summary')}</strong>
          <span>{tp('audit.seen', record.seenSerials.length)}</span>
          <span>{tp('audit.missing', record.missingSerials.length)}</span>
          <span>{tp('audit.unknownCount', record.unknownSerials.length)}</span>
        </div>
      ) : null}
    </section>
  );
};

const PreferencesPanel = ({ snapshot }: { snapshot: InventorySnapshot }) => {
  const { locale, t } = useI18n();
  const { service, perform, busyKeys } = useApp();
  const [exclusions, setExclusions] = useState((snapshot.preferences?.exclusions ?? []).join(', '));
  const [reminderFrequency, setReminderFrequency] = useState(snapshot.preferences?.reminderFrequency ?? 'off');
  const [reminderRecipient, setReminderRecipient] = useState(snapshot.preferences?.reminderRecipient ?? 'in-app');
  const [reminderNotice, setReminderNotice] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setReminderNotice(null);
    await perform(
      'save-preferences',
      async () => {
        let savedRecipient = reminderRecipient;
        const backgroundReminders = new WebPushReminderAdapter();
        if (reminderFrequency !== 'off' && reminderRecipient === 'this-device') {
          const permission = await new BrowserNotificationAdapter().requestPermission();
          if (permission !== 'granted') {
            await backgroundReminders.unsubscribe();
            savedRecipient = 'in-app';
          } else if (!await backgroundReminders.subscribe(
            reminderFrequency,
            locale === 'fr-FR' ? 'fr-FR' : 'en',
          )) savedRecipient = 'in-app';
        } else {
          await backgroundReminders.unsubscribe();
        }
        setReminderRecipient(savedRecipient);
        const saved = await service.savePreferences({
          exclusions: exclusions.split(','),
          alertHorizonDays: 7,
          lastLocationId: snapshot.preferences?.lastLocationId ?? null,
          reminderFrequency,
          reminderRecipient: savedRecipient,
        });
        const noticeKey = reminderDeliveryNoticeKey(
          reminderFrequency,
          reminderRecipient,
          savedRecipient,
        );
        if (noticeKey != null) setReminderNotice(t(noticeKey));
        return saved;
      },
      { success: t('preferences.saved') },
    );
  };

  return (
    <section className="settings-card">
      <div className="settings-card__heading">
        <span className="settings-icon"><Icon name="settings" /></span>
        <div>
          <h2>{t('preferences.title')}</h2>
          <p>{t('preferences.help')}</p>
        </div>
      </div>
      <form className="form-stack" onSubmit={(event) => void submit(event)}>
        <Field
          label={t('preferences.exclusions')}
          value={exclusions}
          onChange={(event) => setExclusions(event.target.value)}
          placeholder={t('preferences.example')}
          hint={t('preferences.exclusionsHint')}
        />
        <div className="form-row">
          <SelectField label={t('preferences.reminderFrequency')} value={reminderFrequency} onChange={(event) => setReminderFrequency(event.target.value as 'off' | 'daily' | 'weekly')}>
            <option value="off">{t('preferences.frequencyOff')}</option>
            <option value="daily">{t('preferences.frequencyDaily')}</option>
            <option value="weekly">{t('preferences.frequencyWeekly')}</option>
          </SelectField>
          <SelectField label={t('preferences.reminderRecipient')} value={reminderRecipient} onChange={(event) => setReminderRecipient(event.target.value as 'in-app' | 'this-device')} disabled={reminderFrequency === 'off'}>
            <option value="in-app">{t('preferences.recipientInApp')}</option>
            <option value="this-device">{t('preferences.recipientDevice')}</option>
          </SelectField>
        </div>
        <p className="field__hint">{t('preferences.reminderLimit')}</p>
        {reminderNotice != null ? <p role="status">{reminderNotice}</p> : null}
        <Button type="submit" disabled={busyKeys.has('save-preferences')}>{t('common.save')}</Button>
      </form>
    </section>
  );
};

const StoragePanel = ({ snapshot }: { snapshot: InventorySnapshot }) => {
  const { locale, t } = useI18n();
  const { service, perform, busyKeys } = useApp();
  const [freezerName, setFreezerName] = useState('');
  const [equipmentKind, setEquipmentKind] = useState<Freezer['equipmentKind']>('four-star');
  const [freezerId, setFreezerId] = useState(snapshot.freezers[0]?.id ?? '');
  const [locationName, setLocationName] = useState('');
  const [parentId, setParentId] = useState('');
  const [deletionOpen, setDeletionOpen] = useState(false);
  const [deletionStep, setDeletionStep] = useState<1 | 2>(1);
  const [deletionPhrase, setDeletionPhrase] = useState('');
  const [deletionMessage, setDeletionMessage] = useState('');
  const household = snapshot.household;

  useEffect(() => {
    if (deletionOpen && deletionStep === 2) {
      document.querySelector<HTMLInputElement>('#household-deletion-confirmation')?.focus();
    }
  }, [deletionOpen, deletionStep]);

  const addFreezer = async (event: FormEvent) => {
    event.preventDefault();
    const freezer = await perform(
      'add-freezer',
      () => service.addFreezer(freezerName, equipmentKind),
      { success: t('storage.applianceAdded') },
    );
    if (freezer != null) {
      setFreezerName('');
      setFreezerId(freezer.id);
    }
  };
  const addLocation = async (event: FormEvent) => {
    event.preventDefault();
    const location = await perform(
      'add-location',
      () => service.addLocation(freezerId, locationName, parentId || null),
      { success: t('storage.locationAdded') },
    );
    if (location != null) {
      setLocationName('');
      setParentId('');
    }
  };

  const closeDeletion = () => {
    if (busyKeys.has('delete-household')) return;
    setDeletionOpen(false);
    setDeletionStep(1);
    setDeletionPhrase('');
    setDeletionMessage('');
  };

  const exportBeforeDeletion = async () => {
    if (household == null) return;
    setDeletionMessage(t('deletion.preparing'));
    try {
      const backup = await createBackup(snapshot, __APP_VERSION__);
      const result = await exportTextFile(
        household.id,
        `frozen-save-avant-suppression-${localToday()}.json`,
        JSON.stringify(backup, null, 2),
        'application/json;charset=utf-8',
      );
      setDeletionMessage(
        result === 'shared'
          ? t('deletion.shared')
          : t('deletion.downloaded'),
      );
    } catch {
      setDeletionMessage(t('backup.exportError'));
    }
  };

  const deleteHousehold = async () => {
    if (household == null || deletionPhrase !== t('deletion.phrase')) return;
    await perform(
      'delete-household',
      () => service.deleteHousehold(household.id),
      { success: t('deletion.success') },
    );
  };

  return (
    <section className="settings-card">
      <div className="settings-card__heading">
        <span className="settings-icon"><Icon name="snow" /></span>
        <div>
          <h2>{t('storage.title')}</h2>
          <p>{t('storage.help')}</p>
        </div>
      </div>
      <div className="storage-overview">
        {snapshot.freezers.map((freezer) => (
          <div key={freezer.id}>
            <strong>{snapshotDisplayLabel(snapshot, freezer.name, locale)}</strong>
            <ul>
              {snapshot.locations.filter((location) => location.freezerId === freezer.id).map((location) => (
                <li key={location.id}>{locationLabel(snapshot, location.id, locale)}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <details>
        <summary>{t('storage.addAppliance')}</summary>
        <form className="form-stack details-form" onSubmit={(event) => void addFreezer(event)}>
          <Field label={t('storage.name')} value={freezerName} onChange={(event) => setFreezerName(event.target.value)} required />
          <SelectField label={t('storage.declaredType')} value={equipmentKind} onChange={(event) => setEquipmentKind(event.target.value as Freezer['equipmentKind'])}>
            <option value="four-star">{t('storage.fourStars')}</option>
            <option value="three-star">{t('storage.threeStars')}</option>
            <option value="fridge">{t('storage.refrigerator')}</option>
            <option value="pantry">{t('storage.pantry')}</option>
            <option value="unknown">{t('storage.unknown')}</option>
          </SelectField>
          <Button type="submit" disabled={busyKeys.has('add-freezer')}>{t('storage.addApplianceSubmit')}</Button>
        </form>
      </details>
      <details>
        <summary>{t('storage.addLocation')}</summary>
        <form className="form-stack details-form" onSubmit={(event) => void addLocation(event)}>
          <SelectField label={t('equipment.appliance')} value={freezerId} onChange={(event) => {
            setFreezerId(event.target.value);
            setParentId('');
          }}>
            {snapshot.freezers.map((freezer) => <option key={freezer.id} value={freezer.id}>{snapshotDisplayLabel(snapshot, freezer.name, locale)}</option>)}
          </SelectField>
          <SelectField label={t('storage.insideOptional')} value={parentId} onChange={(event) => setParentId(event.target.value)}>
            <option value="">{t('storage.directly')}</option>
            {snapshot.locations.filter((location) => location.freezerId === freezerId).map((location) => (
              <option key={location.id} value={location.id}>{locationLabel(snapshot, location.id, locale)}</option>
            ))}
          </SelectField>
          <Field label={t('storage.locationName')} value={locationName} onChange={(event) => setLocationName(event.target.value)} required />
          <Button type="submit" disabled={busyKeys.has('add-location')}>{t('storage.addLocationSubmit')}</Button>
        </form>
      </details>
      <div className="danger-zone">
        <div>
          <strong>{t('deletion.title')}</strong>
          <p>{t('deletion.help')}</p>
        </div>
        <Button
          variant="danger"
          onClick={() => setDeletionOpen(true)}
          disabled={household == null}
          icon={<Icon name="trash" />}
        >
          {t('deletion.open')}
        </Button>
      </div>
      {deletionOpen && household != null ? (
        <Modal
          title={t('deletion.open')}
          description={
            deletionStep === 1
              ? t('deletion.step1')
              : t('deletion.step2')
          }
          onClose={closeDeletion}
        >
          {deletionStep === 1 ? (
            <div className="form-stack">
              <div className="notice notice--danger">
                <Icon name="warning" />
                <div>
                  <strong>{t('deletion.household', { name: snapshotDisplayLabel(snapshot, household.name, locale) })}</strong>
                  <p>{t('deletion.scope')}</p>
                </div>
              </div>
              <div className="notice notice--neutral">
                <Icon name="info" />
                <p>{t('deletion.others')}</p>
              </div>
              <Button
                variant="secondary"
                onClick={() => void exportBeforeDeletion()}
                icon={<Icon name="download" />}
              >
                {t('deletion.exportOptional')}
              </Button>
              {deletionMessage ? (
                <p className="import-message" role="status">{deletionMessage}</p>
              ) : null}
              <div className="modal-actions">
                <Button variant="ghost" onClick={closeDeletion}>{t('common.cancel')}</Button>
                <Button variant="danger" onClick={() => setDeletionStep(2)}>
                  {t('deletion.continue')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="form-stack">
              <p>{t('deletion.confirmInstruction', { name: snapshotDisplayLabel(snapshot, household.name, locale), phrase: t('deletion.phrase') })}</p>
              <Field
                id="household-deletion-confirmation"
                label={t('deletion.confirmLabel')}
                value={deletionPhrase}
                onChange={(event) => setDeletionPhrase(event.target.value)}
                autoComplete="off"
                spellCheck={false}
                aria-describedby="household-deletion-confirmation-hint"
              />
              <p id="household-deletion-confirmation-hint" className="field__hint">
                {t('deletion.backupOptional')}
              </p>
              <div className="modal-actions">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setDeletionStep(1);
                    setDeletionPhrase('');
                  }}
                  disabled={busyKeys.has('delete-household')}
                >
                  {t('common.back')}
                </Button>
                <Button
                  variant="danger"
                  onClick={() => void deleteHousehold()}
                  disabled={
                    deletionPhrase !== t('deletion.phrase') || busyKeys.has('delete-household')
                  }
                >
                  {t('deletion.final')}
                </Button>
              </div>
            </div>
          )}
        </Modal>
      ) : null}
    </section>
  );
};

export const BackupPanel = ({ snapshot }: { snapshot: InventorySnapshot }) => {
  const i18n = useI18n();
  const { t } = i18n;
  const { service, perform, busyKeys } = useApp();
  const [importMessage, setImportMessage] = useState('');
  const [importPhase, setImportPhase] = useState<'reading' | 'copying' | 'restoring' | null>(null);
  const importGeneration = useRef(0);
  const currentSnapshot = useRef(snapshot);
  useLayoutEffect(() => { currentSnapshot.current = snapshot; }, [snapshot]);
  useEffect(() => () => { importGeneration.current += 1; }, []);

  const cancelImport = () => {
    importGeneration.current += 1;
    setImportPhase(null);
    setImportMessage(t('backup.cancelled'));
  };

  const exportJson = async () => {
    setImportMessage(t('backup.preparing'));
    try {
      const backup = await createBackup(snapshot, __APP_VERSION__);
      const result = await exportTextFile(
        snapshot.household?.id ?? '',
        `frozen-save-${localToday()}.json`,
        JSON.stringify(backup, null, 2),
        'application/json;charset=utf-8',
      );
      setImportMessage(
        result === 'shared'
          ? t('backup.shared')
          : t('backup.downloaded'),
      );
    } catch {
      setImportMessage(t('backup.exportError'));
    }
  };

  const exportCsv = async () => {
    setImportMessage(t('backup.exportPreparing'));
    try {
      const result = await exportTextFile(
        snapshot.household?.id ?? '',
        `frozen-save-${localToday()}.csv`,
        createCsv(snapshot.containers),
        'text/csv;charset=utf-8',
      );
      setImportMessage(
        result === 'shared'
          ? t('backup.exportShared')
          : t('backup.csvDownloaded'),
      );
    } catch {
      setImportMessage(t('backup.exportError'));
    }
  };

  const importFile = async (file: File | null) => {
    if (file == null) return;
    const generation = ++importGeneration.current;
    const expected = snapshot;
    const active = () => importGeneration.current === generation;
    const unchanged = () => JSON.stringify(currentSnapshot.current) === JSON.stringify(expected);
    setImportPhase('reading');
    setImportMessage(t('backup.verifying'));
    try {
      const backup = await parseBackup(await file.text());
      if (!active()) return;
      if (!unchanged()) {
        setImportMessage(t('backup.importChanged'));
        return;
      }
      const confirmed = window.confirm(
        t('backup.restoreConfirm', { date: i18n.formatDate(new Date(backup.exportedAt), { dateStyle: 'long', timeStyle: 'short' }) }),
      );
      if (!confirmed) {
        setImportMessage(t('backup.cancelled'));
        return;
      }
      setImportPhase('copying');
      setImportMessage(t('backup.safetyCopyPreparing'));
      try {
        const safetyCopy = await createBackup(expected, __APP_VERSION__);
        if (!active()) return;
        if (!unchanged()) {
          setImportMessage(t('backup.importChanged'));
          return;
        }
        await exportTextFile(
          snapshot.household?.id ?? '',
          `frozen-save-avant-restauration-${localToday()}.json`,
          JSON.stringify(safetyCopy, null, 2),
          'application/json;charset=utf-8',
        );
      } catch {
        if (active()) setImportMessage(t('backup.safetyCopyFailed'));
        return;
      }
      if (!active()) return;
      if (!unchanged()) {
        setImportMessage(t('backup.importChanged'));
        return;
      }
      setImportPhase('restoring');
      const restored = await perform(
        'restore-backup',
        () => service.restoreBackup(backup.data, expected),
        { success: t('backup.restoreSuccess') },
      );
      if (active()) setImportMessage(restored === null ? t('backup.restoreFailed') : t('backup.restored'));
    } catch {
      if (active()) setImportMessage(t('backup.invalid'));
    } finally {
      if (active()) setImportPhase(null);
    }
  };

  return (
    <section className="settings-card">
      <div className="settings-card__heading">
        <span className="settings-icon"><Icon name="download" /></span>
        <div>
          <h2>{t('backup.title')}</h2>
          <p>{t('backup.help')}</p>
        </div>
      </div>
      <div className="button-grid">
        <Button variant="secondary" onClick={() => void exportJson()} icon={<Icon name="download" />}>
          {t('backup.json')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => void exportCsv()}
          icon={<Icon name="download" />}
        >
          {t('backup.csv')}
        </Button>
      </div>
      <div className="notice notice--neutral">
        <Icon name="info" />
        <p>{t('backup.restoreWarning')}</p>
      </div>
      <label className={`file-drop ${busyKeys.has('restore-backup') ? 'is-disabled' : ''}`}>
        <Icon name="upload" />
        <span><strong>{t('backup.restore')}</strong><small>{t('backup.restoreHelp')}</small></span>
        <input
          type="file"
          accept="application/json,.json"
          disabled={importPhase === 'restoring' || busyKeys.has('restore-backup')}
          onChange={(event) => {
            const file = event.currentTarget.files?.[0] ?? null;
            event.currentTarget.value = '';
            void importFile(file);
          }}
        />
      </label>
      {importPhase === 'reading' || importPhase === 'copying' ? (
        <Button variant="ghost" onClick={cancelImport}>{t('common.cancel')}</Button>
      ) : null}
      {importMessage ? <p className="import-message" role="status">{importMessage}</p> : null}
    </section>
  );
};

const DemoPanel = ({ snapshot }: { snapshot: InventorySnapshot }) => {
  const { t } = useI18n();
  const { service, perform, busyKeys, runtime } = useApp();
  if (snapshot.meta.demoSeededAt == null) return null;

  const reset = async () => {
    const confirmed = window.confirm(
      t('demo.resetConfirm'),
    );
    if (!confirmed) return;
    await perform('reset-demo', async () => {
      await service.resetDemo();
      if (runtime.mode === 'demo') {
        return service.loadDemo();
      }
    }, {
      success:
        runtime.mode === 'demo'
          ? t('demo.resetSuccess')
          : t('demo.clearSuccess'),
    });
  };

  return (
    <section className="settings-card settings-card--demo" data-tour-id="demo-controls">
      <div className="settings-card__heading">
        <span className="settings-icon"><Icon name="info" /></span>
        <div>
          <h2>{t('demo.panelTitle')}</h2>
          <p>{t('demo.panelHelp')}</p>
        </div>
      </div>
      <p className="muted">
        {runtime.mode === 'demo'
          ? t('demo.modeHelp')
          : t('demo.seedHelp')}
      </p>
      <Button variant="secondary" onClick={() => void reset()} disabled={busyKeys.has('reset-demo')}>
        {runtime.mode === 'demo' ? t('demo.reset') : t('demo.clear')}
      </Button>
    </section>
  );
};

const DiagnosticPanel = ({ snapshot }: { snapshot: InventorySnapshot }) => {
  const i18n = useI18n();
  const { t } = i18n;
  const { service, perform, busyKeys } = useApp();
  const [estimate, setEstimate] = useState<{ usage: number | null; quota: number | null } | null>(null);

  useEffect(() => {
    void getStorageEstimate().then(setEstimate);
  }, [snapshot.meta.updatedAt]);

  const requestStorage = async () => {
    await perform(
      'persistent-storage',
      async () => {
        const status = await requestPersistentStorage();
        await service.savePersistentStorageStatus(status);
      },
      { success: t('diagnostic.storageUpdated') },
    );
  };

  const formatBytes = (value: number | null): string =>
    value == null ? t('diagnostic.unavailable') : t('diagnostic.megabytes', { value: i18n.formatNumber(value / 1_048_576, { maximumFractionDigits: 1 }) });

  return (
    <section className="settings-card">
      <div className="settings-card__heading">
        <span className="settings-icon"><Icon name="info" /></span>
        <div>
          <h2>{t('diagnostic.title')}</h2>
          <p>{t('diagnostic.help')}</p>
        </div>
      </div>
      <dl className="diagnostic-list">
        <div><dt>{t('diagnostic.version')}</dt><dd>{__APP_VERSION__}</dd></div>
        <div><dt>{t('diagnostic.build')}</dt><dd>{__BUILD_ID__ === 'local-reproducible' ? t('diagnostic.localBuild') : __BUILD_ID__}</dd></div>
        <div>
          <dt>{t('diagnostic.buildTime')}</dt>
          <dd>
            {__BUILD_TIME__ == null
              ? t('diagnostic.notProvided')
              : i18n.formatDate(new Date(__BUILD_TIME__), { dateStyle: 'medium', timeStyle: 'short' })}
          </dd>
        </div>
        <div><dt>{t('diagnostic.schema')}</dt><dd>{t('diagnostic.versionValue', { version: snapshot.meta.schemaVersion })}</dd></div>
        <div><dt>{t('diagnostic.backupFormat')}</dt><dd>{t('diagnostic.versionValue', { version: BACKUP_FORMAT_VERSION })}</dd></div>
        <div><dt>{t('diagnostic.mode')}</dt><dd>{t('diagnostic.localMode')}</dd></div>
        <div><dt>{t('diagnostic.persistentStorage')}</dt><dd>{t(`diagnostic.storageStatus.${snapshot.meta.persistentStorage}` as MessageKey)}</dd></div>
        <div><dt>{t('diagnostic.space')}</dt><dd>{formatBytes(estimate?.usage ?? null)} / {formatBytes(estimate?.quota ?? null)}</dd></div>
        <div><dt>{t('diagnostic.containersEvents')}</dt><dd>{snapshot.containers.length} / {snapshot.events.length}</dd></div>
        <div><dt>{t('diagnostic.readingsIncidents')}</dt><dd>{snapshot.temperatureReadings.length} / {snapshot.temperatureIncidents.length}</dd></div>
      </dl>
      {snapshot.meta.persistentStorage !== 'granted' ? (
        <Button variant="secondary" onClick={() => void requestStorage()} disabled={busyKeys.has('persistent-storage')}>
          {t('diagnostic.requestStorage')}
        </Button>
      ) : null}
      <div className="notice notice--neutral">
        <Icon name="info" />
        <p>{t('diagnostic.noRemote')}</p>
      </div>
    </section>
  );
};

const InstallPanel = () => {
  const { t } = useI18n();
  return <section className="settings-card">
    <div className="settings-card__heading">
      <span className="settings-icon"><Icon name="download" /></span>
      <div>
        <h2>{t('install.panelTitle')}</h2>
        <p>{t('install.panelHelp')}</p>
      </div>
    </div>
    <PwaInstallButton compact />
  </section>;
};

const MorePage = ({ snapshot }: { snapshot: InventorySnapshot }) => {
  const { t } = useI18n();
  const { service, runtime } = useApp();
  return <div className="page">
    <SectionHeader
      eyebrow={t('more.eyebrow')}
      title={t('more.title')}
      description={t('more.description')}
    />
    <div className="settings-grid">
      <DemoPanel snapshot={snapshot} />
      <HouseholdSharingPanel />
      {runtime.mode === 'app' && service.handoffAvailable() ? <section className="settings-card">
        <OfficialHandoffReceipt load={intentId => service.exportArchibaldReceipt(null, runtime.mode, intentId)} />
      </section> : null}
      <TemperaturePanel snapshot={snapshot} />
      <AuditPanel snapshot={snapshot} />
      <PreferencesPanel snapshot={snapshot} />
      <StoragePanel snapshot={snapshot} />
      <InstallPanel />
      <BackupPanel snapshot={snapshot} />
      <DiagnosticPanel snapshot={snapshot} />
      <section className="settings-card">
        <div className="settings-card__heading">
          <span className="settings-icon"><Icon name="info" /></span>
          <div>
            <h2>{t('safety.title')}</h2>
            <p>{t('safety.help')}</p>
          </div>
        </div>
        <ul className="source-list">
          {OFFICIAL_SAFETY_SOURCES.map((source, index) => (
            <li key={source.url}><a href={source.url} target="_blank" rel="noreferrer">{t(`safety.source.${['agriculture', 'anses', 'dgccrf'][index]}` as MessageKey)}</a></li>
          ))}
        </ul>
      </section>
      <section className="settings-card">
        <div className="settings-card__heading">
          <span className="settings-icon"><Icon name="info" /></span>
          <div>
            <h2>{t('help.title')}</h2>
            <p>{t('help.description')}</p>
          </div>
        </div>
        <ol className="source-list">
          <li>{t('help.step1')}</li>
          <li>{t('help.step2')}</li>
          <li>{t('help.step3')}</li>
        </ol>
      </section>
    </div>
  </div>;
};

export const Navigation = ({ view, open, onNavigate, onClose }: {
  view: View;
  open: boolean;
  onNavigate: (view: View) => void;
  onClose: () => void;
}) => {
  const { t } = useI18n();
  const { sharing, snapshot } = useApp();
  const [myDohmBusy, setMyDohmBusy] = useState(false);
  const [myDohmError, setMyDohmError] = useState(false);
  const openMyDohm = async () => {
    if (myDohmBusy) return;
    setMyDohmBusy(true);
    setMyDohmError(false);
    try {
      const native = await sharing.status(snapshot?.household?.id ?? null);
      if (native.state === 'disconnected' || native.state === 'standalone') {
        window.location.assign(MY_DOHM_MENU_ENTRY.href);
        return;
      }
      if (native.state !== 'connected') throw new Error('MY_DOHM_UNAVAILABLE');
      const contact = await myDohmFederation.status();
      if (['missing_contact', 'contact_verification_available', 'pending_contact_verification', 'contact_verification_queued'].includes(contact.state)) {
        onNavigate('more');
        onClose();
        return;
      }
      if (contact.nativeEntryReady === true) {
        window.location.assign(`${myDohmPortalOrigin(window.location.origin)}/api/v1/member/product-entry/start?productId=frozen-save`);
        return;
      }
      setMyDohmError(true);
    } catch {
      setMyDohmError(true);
    } finally {
      setMyDohmBusy(false);
    }
  };
  const links: Array<{ id: View; label: string; icon: Parameters<typeof Icon>[0]['name'] }> = [
    { id: 'home', label: t('nav.home'), icon: 'home' },
    { id: 'fridge', label: t('nav.fridge'), icon: 'snow' },
    { id: 'inventory', label: t('nav.inventory'), icon: 'box' },
    { id: 'priorities', label: t('nav.priorities'), icon: 'clock' },
    { id: 'meals', label: t('nav.meals'), icon: 'meal' },
    { id: 'more', label: t('nav.more'), icon: 'more' },
  ];
  return (
    <>
      <button type="button" className={`app-nav-backdrop${open ? ' is-open' : ''}`} aria-label={t('app.closeMenu')} tabIndex={open ? 0 : -1} onClick={onClose} />
      <nav id="frozen-save-menu" className={`app-nav${open ? ' is-open' : ''}`} aria-label={t('app.primaryNavigation')}>
        <div className="app-nav__header">
          <strong>Frozen Save</strong>
          <button type="button" className="icon-button" onClick={onClose} aria-label={t('app.closeMenu')}><Icon name="close" /></button>
        </div>
        {links.map((link) => (
          <button
            type="button"
            key={link.id}
            className={view === link.id ? 'is-active' : ''}
            aria-current={view === link.id ? 'page' : undefined}
            onClick={() => {
              onNavigate(link.id);
              onClose();
            }}
          >
            <Icon name={link.icon} />
            <span>{link.label}</span>
          </button>
        ))}
        <button
          type="button"
          disabled={myDohmBusy}
          data-contract-version={MY_DOHM_MENU_ENTRY.contractVersion}
          onClick={() => void openMyDohm()}
        >
          <Icon name="info" />
          <span>{t('nav.myDohm')}</span>
        </button>
        {myDohmError ? <p role="status">{t('nav.myDohmUnavailable')}</p> : null}
      </nav>
    </>
  );
};

export const App = () => {
  const { snapshot, loading, toast, undo, dismissToast, runtime } = useApp();
  const { locale, t } = useI18n();
  const [view, setView] = useState<View>(new URL(window.location.href).searchParams.get('connexion') === '1'
    ? 'more' : runtime.recipeReturn == null ? 'home' : 'meals');
  const [recipeFocusContainerId, setRecipeFocusContainerId] = useState<string | null>(null);
  const [recipeReturn, setRecipeReturn] = useState(runtime.recipeReturn);
  const [addOpen, setAddOpen] = useState(false);
  const [addFamily, setAddFamily] = useState<'fridge' | 'freezer' | null>(null);
  const [quickModal, setQuickModal] = useState<QuickModal>(null);
  const [exitPrefillId, setExitPrefillId] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [guidedTourOpen, setGuidedTourOpen] = useState(false);
  const pwa = usePwaUpdates();
  const navigate = useCallback((next: View) => {
    if (next === 'home') setExitPrefillId(null);
    if (next !== 'meals') {
      setRecipeFocusContainerId(null);
      setRecipeReturn(null);
    }
    setView(next);
    setMenuOpen(false);
    document.getElementById('contenu')?.scrollTo({
      top: 0,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
    });
  }, []);
  if (loading || snapshot == null) {
    return (
      <main id="contenu" className="loading-screen" aria-busy="true">
        <div className="brand-mark brand-mark--large" aria-hidden="true"><img src="/icon.svg" alt="" /></div>
        <p role="status">{t('app.loading')}</p>
      </main>
    );
  }
  if (snapshot.household == null) {
    return (
      <>
        <PwaUpdateBanner pwa={pwa} />
        <SetupScreen />
        {toast != null ? (
          <div
            className={`toast toast--${toast.tone}`}
            role={toast.tone === 'error' ? 'alert' : 'status'}
          >
            <span>{toast.message}</span>
            <button
              type="button"
              className="icon-button"
              onClick={dismissToast}
              aria-label={t('app.closeMessage')}
            >
              <Icon name="close" />
            </button>
          </div>
        ) : null}
      </>
    );
  }

  const modalContainer =
    quickModal == null
      ? null
      : snapshot.containers.find((container) => container.id === quickModal.containerId) ?? null;
  const startExit = (container: FoodContainer) => {
    setQuickModal(null);
    navigate('home');
    setExitPrefillId(container.id);
  };
  const openRecipeIdeas = (container: FoodContainer) => {
    setRecipeReturn(null);
    setRecipeFocusContainerId(container.id);
    navigate('meals');
  };

  return (
    <div className={`app-shell${runtime.mode === 'demo' ? ' app-shell--demo' : ''}`}>
      {runtime.mode === 'demo' ? (
        <div className="public-demo-banner" role="status">
          <strong>{t('demo.banner')}</strong>
          <span>{t('demo.detail')}</span>
          <button type="button" onClick={() => setGuidedTourOpen(true)}>{t('demo.tour.start')}</button>
          <a href={runtime.appPath}>{t('demo.openApp')}</a>
        </div>
      ) : null}
      <header className="topbar">
        <button type="button" className="menu-toggle" aria-label={t('app.openMenu')} aria-controls="frozen-save-menu" aria-expanded={menuOpen} onClick={() => setMenuOpen(true)}><Icon name="more" /></button>
        <button className="brand" type="button" onClick={() => navigate('home')} aria-label={t('app.backHome')}>
          <span className="brand-mark" aria-hidden="true"><img src="/icon.svg" alt="" /></span>
          <span><strong>Frozen Save</strong><small>{snapshotDisplayLabel(snapshot, snapshot.household.name, locale)}</small></span>
        </button>
        <div className="topbar-statuses">
          <LanguageSelector compact />
          <span className="test-version-badge">{
            runtime.mode === 'demo'
              ? t('app.demo')
              : t('app.noAccount')
          }</span>
          <div className="local-status"><span aria-hidden="true" /><span>{t('app.savedOnDevice')}</span></div>
        </div>
      </header>

      <PwaUpdateBanner pwa={pwa} />
      {pwa.offlineReady ? (
        <div className="offline-banner" role="status">
          <span>{t('app.offlineReady')}</span>
          <button type="button" className="icon-button" onClick={pwa.dismissOfflineReady} aria-label={t('app.close')}><Icon name="close" /></button>
        </div>
      ) : null}

      <main id="contenu" className="app-content-scroll">
        {view === 'home' ? (
          <HomePage
            snapshot={snapshot}
            onAdd={() => {
              setAddFamily(null);
              setAddOpen(true);
            }}
            onOpen={setQuickModal}
            onNavigate={navigate}
            exitPrefillId={exitPrefillId}
          />
        ) : null}
        {view === 'fridge' ? <FridgePage snapshot={snapshot} onAdd={() => {
          setAddFamily('fridge');
          setAddOpen(true);
        }} onOpen={setQuickModal} onNavigate={navigate} /> : null}
        {view === 'inventory' ? <InventoryPage snapshot={snapshot} onAdd={() => {
          setAddFamily(null);
          setAddOpen(true);
        }} onOpen={setQuickModal} onExit={startExit} /> : null}
        {view === 'priorities' ? (
          <PrioritiesPage
            snapshot={snapshot}
            onOpen={setQuickModal}
            onOpenRecipes={openRecipeIdeas}
          />
        ) : null}
        {view === 'meals' ? (
          <MealsPage
            snapshot={snapshot}
            onOpen={setQuickModal}
            focusContainerId={recipeFocusContainerId}
            recipeReturn={recipeReturn}
            onRecipeReturnHandled={() => setRecipeReturn(null)}
          />
        ) : null}
        {view === 'more' ? <MorePage snapshot={snapshot} /> : null}
      </main>

      <Navigation view={view} open={menuOpen} onNavigate={navigate} onClose={() => setMenuOpen(false)} />

      {runtime.mode === 'demo' && guidedTourOpen ? (
        <GuidedDemoTour
          onNavigate={(next: GuidedDemoView) => navigate(next)}
          onClose={() => setGuidedTourOpen(false)}
        />
      ) : null}

      {toast != null ? (
        <div className={`toast toast--${toast.tone}`} role={toast.tone === 'error' ? 'alert' : 'status'}>
          <span>{toast.message}</span>
          {toast.undoEvent != null &&
          toast.undoEvent.eventType !== 'undo' &&
          toast.undoEvent.payload.before != null ? (
            <button type="button" onClick={() => void undo(toast.undoEvent!)}>{t('app.undo')}</button>
          ) : null}
          <button type="button" className="icon-button" onClick={dismissToast} aria-label={t('app.closeMessage')}><Icon name="close" /></button>
        </div>
      ) : null}

      {addOpen ? <AddContainerModal snapshot={snapshot} preferredFamily={addFamily} onClose={() => {
        setAddOpen(false);
        setAddFamily(null);
      }} /> : null}
      {modalContainer != null && quickModal?.type === 'detail' ? (
        <ContainerDetailModal container={modalContainer} snapshot={snapshot} onClose={() => setQuickModal(null)} onStartExit={() => startExit(modalContainer)} />
      ) : null}
      {modalContainer != null && quickModal?.type === 'partial' ? (
        <PartialModal container={modalContainer} snapshot={snapshot} onClose={() => setQuickModal(null)} />
      ) : null}
      {modalContainer != null && quickModal?.type === 'move' ? (
        <MoveModal container={modalContainer} snapshot={snapshot} onClose={() => setQuickModal(null)} />
      ) : null}
      {modalContainer != null && quickModal?.type === 'discard' ? (
        <DiscardModal container={modalContainer} snapshot={snapshot} onClose={() => setQuickModal(null)} />
      ) : null}
    </div>
  );
};
